import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import {
  AlertCircle,
  BookOpen,
  Boxes,
  CalendarClock,
  ClipboardList,
  FolderTree,
  GraduationCap,
  IndianRupee,
  Target,
  UserCog,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import {
  checkPermission,
  getModuleActions,
} from "../../../Configurations/PermissionConfigrations";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import {
  applyEmployeeTrainerPhotos,
  buildEmployeePhotoLookup,
} from "../../../Components/Table/ListPersonAvatar";
import BatchModulesGroupedView from "./BatchModulesGroupedView";
import ProgramMonthlyTrafficCharts from "./ProgramMonthlyTrafficCharts";
import TrainerModulesGroupedView from "./TrainerModulesGroupedView";
import Layout4 from "../../DataLayouts/Layout4";
import AdmissionFeePortalDialog from "./AdmissionFeePortalDialog";
import BatchClassAttendanceViewDialog from "./BatchClassAttendanceViewDialog";
import StudentAttendanceViewDialog from "./StudentAttendanceViewDialog";
import StudentDetailDialog from "./StudentDetailDialog";
import {
  emsBatchListRowMatchesLoggedInTrainer,
  emsClassRecordMatchesLoggedInTrainer,
  emsRowMatchesLoggedInTrainer,
} from "../../../Utils/emsTrainerUserMatch";
import { seesOnlyOwnBatches } from "../../../Utils/trainerPortal";
import { buildMetadataFiltersFromSearchParams } from "../../../Utils/metadataListFilters";
import { resolveMetadataSortField } from "../../../Utils/metadataListSort";
import { getLeadEnrollmentBlockMessage } from "../../../Utils/leadEnrollment";
import { isWithinLeadContactWindows } from "./LeadListContactCell";
import { fetchInstituteSettings } from "../../../Apis/InstituteSettings-api";

const LIST_WIDGET_VISUALS = {
  open_leads: { icon: Target, tone: "" },
  leads_this_month: { icon: Users, tone: "" },
  qualified_leads_this_month: { icon: Target, tone: "ok" },
  admissions_this_month: { icon: ClipboardList, tone: "ok" },
  active_students: { icon: GraduationCap, tone: "" },
  new_admissions_this_month: { icon: UserPlus, tone: "ok" },
  course_completed: { icon: GraduationCap, tone: "ok" },
  inactive_students: { icon: Users, tone: "warn" },
  fee_collected_this_month: { icon: IndianRupee, tone: "ok", valueTone: "ok" },
  fee_due_this_month: { icon: CalendarClock, tone: "bad", valueTone: "bad" },
  overdue_fee: { icon: AlertCircle, tone: "bad", valueTone: "bad" },
  today_collection: { icon: Wallet, tone: "ok", valueTone: "ok" },
  active_batches: { icon: Boxes, tone: "" },
  starting_soon: { icon: CalendarClock, tone: "ok" },
  ending_soon: { icon: CalendarClock, tone: "warn" },
  hold_batches: { icon: Boxes, tone: "warn" },
  training_categories: { icon: FolderTree, tone: "" },
  training_programs: { icon: BookOpen, tone: "ok" },
  trainers: { icon: UserCog, tone: "" },
  students: { icon: GraduationCap, tone: "" },
};
const METADATA_DEFAULT_URL_PARAMS = {
  ems_leads: {
    page: "1",
    pageSize: "25",
    sortBy: "lead_datetime",
    sortOrder: "desc",
    lead_status: "open",
  },
  ems_lead_activities: {
    page: "1",
    pageSize: "25",
    sortBy: "activity_datetime",
    sortOrder: "desc",
  },
  ems_lead_contact_timings: {
    page: "1",
    pageSize: "25",
    sortBy: "organization_ems_lead_contact_timing_id",
    sortOrder: "desc",
  },
  ems_demo_sessions: {
    page: "1",
    pageSize: "25",
    sortBy: "demo_date",
    sortOrder: "desc",
  },
  ems_demo_participants: {
    page: "1",
    pageSize: "25",
    sortBy: "organization_ems_demo_participant_id",
    sortOrder: "desc",
  },
  ems_students: {
    page: "1",
    pageSize: "25",
    sortBy: "created_at",
    sortOrder: "desc",
  },
  ems_admissions: {
    page: "1",
    pageSize: "25",
    sortBy: "admission_date",
    sortOrder: "desc",
  },
  ems_fee_installments: {
    page: "1",
    pageSize: "25",
    sortBy: "due_date",
    sortOrder: "asc",
  },
  ems_student_fees: {
    page: "1",
    pageSize: "25",
    sortBy: "payment_date",
    sortOrder: "desc",
  },
  ems_batches: {
    page: "1",
    pageSize: "25",
    sortBy: "start_date",
    sortOrder: "desc",
  },
  ems_batch_modules: {
    page: "1",
    pageSize: "25",
    sortBy: "sequence_no",
    sortOrder: "asc",
  },
  ems_batch_students: {
    page: "1",
    pageSize: "25",
    sortBy: "enrollment_date",
    sortOrder: "desc",
    batch_status: "all",
  },
  ems_batch_classes: {
    page: "1",
    pageSize: "25",
    sortBy: "start_time",
    sortOrder: "asc",
    batch_activity: "active",
  },
  ems_batch_class_records: {
    page: "1",
    pageSize: "25",
    sortBy: "start_time",
    sortOrder: "desc",
  },
  ems_class_attendance: {
    page: "1",
    pageSize: "25",
    sortBy: "attendance_date",
    sortOrder: "desc",
  },
  ems_assessments: {
    page: "1",
    pageSize: "25",
    sortBy: "organization_ems_assessment_id",
    sortOrder: "desc",
  },
  ems_assessment_results: {
    page: "1",
    pageSize: "25",
    sortBy: "organization_ems_assessment_result_id",
    sortOrder: "desc",
  },
  ems_training_programs: {
    page: "1",
    pageSize: "25",
    sortBy: "training_program_name",
    sortOrder: "asc",
  },
  ems_training_program_categories: {
    page: "1",
    pageSize: "25",
    sortBy: "training_program_category_name",
    sortOrder: "asc",
  },
  ems_training_program_modules: {
    page: "1",
    pageSize: "25",
    sortBy: "category",
    sortOrder: "asc",
  },
  ems_module_questions: {
    page: "1",
    pageSize: "25",
    sortBy: "category",
    sortOrder: "asc",
  },
  ems_certificates: {
    page: "1",
    pageSize: "25",
    sortBy: "issue_date",
    sortOrder: "desc",
  },
  ems_recruitment_agencies: {
    page: "1",
    pageSize: "25",
    sortBy: "agency_name",
    sortOrder: "asc",
  },
  ems_companies: {
    page: "1",
    pageSize: "25",
    sortBy: "company_name",
    sortOrder: "asc",
  },
  ems_placement_referrals: {
    page: "1",
    pageSize: "25",
    sortBy: "referral_date",
    sortOrder: "desc",
  },
  ems_student_review_platforms: {
    page: "1",
    pageSize: "25",
    sortBy: "student_review_platform_name",
    sortOrder: "asc",
  },
  ems_student_reviews: {
    page: "1",
    pageSize: "25",
    sortBy: "review_requested_date",
    sortOrder: "desc",
  },
  ems_lead_stages: {
    page: "1",
    pageSize: "25",
    sortBy: "lead_stage_sequence_number",
    sortOrder: "asc",
  },
  ems_lead_sources: {
    page: "1",
    pageSize: "25",
    sortBy: "lead_source_name",
    sortOrder: "asc",
  },
  ems_training_spaces: {
    page: "1",
    pageSize: "25",
    sortBy: "space_name",
    sortOrder: "asc",
  },
};

const FALLBACK_METADATA_DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "25",
};

function resolveMetadataDefaultUrlParams(filterModule, tableName) {
  const key = filterModule || tableName;
  return (
    METADATA_DEFAULT_URL_PARAMS[key] ||
    METADATA_DEFAULT_URL_PARAMS[tableName] ||
    FALLBACK_METADATA_DEFAULT_URL_PARAMS
  );
}

function resolveMetadataDefaultSortField(filterModule, tableName) {
  const params = resolveMetadataDefaultUrlParams(filterModule, tableName);
  return params.sortBy || "id";
}

function sortComputedMetadataRows(rows, sortField, sortDirection) {
  if (!Array.isArray(rows) || sortField !== "contact_status_dot") {
    return rows;
  }

  return [...rows].sort((a, b) => {
    const aActive = isWithinLeadContactWindows(
      String(a?.__lct_timezone || "").trim(),
      a?.__lct_morning_start,
      a?.__lct_morning_end,
      a?.__lct_evening_start,
      a?.__lct_evening_end
    )
      ? 1
      : 0;
    const bActive = isWithinLeadContactWindows(
      String(b?.__lct_timezone || "").trim(),
      b?.__lct_morning_start,
      b?.__lct_morning_end,
      b?.__lct_evening_start,
      b?.__lct_evening_end
    )
      ? 1
      : 0;

    return sortDirection === "asc" ? aActive - bActive : bActive - aActive;
  });
}

const DEFAULT_METADATA_PAGINATION = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

/** Self-only trainer lists filter rows client-side; must not use org-wide server totals. */
function isEmsTrainerSelfScopedList(apiEndpoint, Permission, userData) {
  if (!apiEndpoint) return false;
  const has = (action) => checkPermission(Permission, action);
  const selfOnly = (selfAction, allAction) => has(selfAction) && !has(allAction);

  if (
    apiEndpoint.includes("/api/ems/demo-sessions") &&
    !apiEndpoint.includes("/api/ems/demo-participants")
  ) {
    return selfOnly("VIEW_SELF_DEMO_SESSIONS", "VIEW_DEMO_SESSIONS");
  }
  if (apiEndpoint.includes("/api/ems/demo-participants")) {
    return selfOnly("VIEW_SELF_DEMO_PARTICIPANTS", "VIEW_DEMO_PARTICIPANTS");
  }
  if (
    apiEndpoint.includes("/api/ems/batches") &&
    !apiEndpoint.includes("/api/ems/batch-")
  ) {
    return seesOnlyOwnBatches(Permission, userData);
  }
  if (apiEndpoint.includes("/api/ems/batch-classes")) {
    return selfOnly("VIEW_SELF_BATCH_CLASSES", "VIEW_BATCH_CLASS");
  }
  if (apiEndpoint.includes("/api/ems/batch-students")) {
    return selfOnly("VIEW_SELF_BATCH_STUDENTS", "VIEW_BATCH_STUDENTS");
  }
  if (apiEndpoint.includes("/api/ems/class-records")) {
    return selfOnly("VIEW_SELF_BATCH_CLASS_RECORDS", "VIEW_BATCH_CLASS_RECORD");
  }
  if (apiEndpoint.includes("/api/ems/classes-attendance")) {
    return selfOnly("VIEW_SELF_CLASS_ATTENDANCE", "VIEW_CLASS_ATTENDANCES");
  }
  return false;
}

const SELF_SCOPED_FETCH_PAGE_SIZE = 100; // metadata engine max limit is 100
const SELF_SCOPED_FETCH_MAX_PAGES = 50;

/** Parses Lead list cell datetime across list/API formats. */
function parseLeadListDateTime(str) {
  if (!str || str === "-") return null;
  const value = String(str).trim();

  // Display format from FormConfigurations.formatDateTime: DD-MM-YYYY h:mm AM/PM
  let m = value.match(
    /^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i
  );
  if (m) {
    const [, d, mo, y, h12, mi, ampmRaw] = m;
    const ampm = String(ampmRaw).toUpperCase();
    let h = Number(h12) % 12;
    if (ampm === "PM") h += 12;
    return new Date(Number(y), Number(mo) - 1, Number(d), h, Number(mi), 0);
  }

  // Legacy/alternate display format: DD-MM-YYYY HH:mm:ss
  m = value.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (m) {
    const [, d, mo, y, h, mi, s] = m;
    return new Date(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(s)
    );
  }

  // API/server format often arrives as YYYY-MM-DD HH:mm:ss
  m = value.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (m) {
    const [, y, mo, d, h, mi, s] = m;
    return new Date(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(s)
    );
  }

  // Last fallback for ISO-ish strings.
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Client-side filter for /leads when opened with ?period=today|month (matches displayed lead_datetime). */
function filterLeadsRowsByPeriod(rows, period) {
  if (!rows?.length || !period) return rows;
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart);
  todayEnd.setHours(23, 59, 59, 999);

  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );

  return rows.filter((row) => {
    const dt = parseLeadListDateTime(row.lead_datetime);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    if (period === "today") {
      return dt >= todayStart && dt <= todayEnd;
    }
    if (period === "month") {
      return dt >= monthStart && dt <= monthEnd;
    }
    return true;
  });
}

/** Parses demo session list cell `DD-MM-YYYY` (FormatDateV1 in FormConfigurations). */
function parseDemoDateCell(str) {
  if (!str || str === "-") return null;
  const m = String(str).trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
}

/** Client-side filter for /demo-sessions when opened with ?period=today. */
function filterDemoSessionsRowsToday(rows) {
  if (!rows?.length) return rows;
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const day = now.getDate();
  return rows.filter((row) => {
    const dt = parseDemoDateCell(row.demo_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return (
      dt.getFullYear() === y &&
      dt.getMonth() === m &&
      dt.getDate() === day
    );
  });
}

/** Client-side filter for /demo-sessions when opened with ?period=month (and optional ?month=YYYY-MM). */
function filterDemoSessionsRowsByMonth(rows, monthYYYYMM) {
  if (!rows?.length) return rows;
  let start;
  let end;

  if (monthYYYYMM && typeof monthYYYYMM === "string" && monthYYYYMM.includes("-")) {
    const match = monthYYYYMM.trim().match(/^(\d{4})-(\d{2})$/);
    if (match) {
      const [, y, mo] = match;
      start = new Date(Number(y), Number(mo) - 1, 1, 0, 0, 0, 0);
      end = new Date(Number(y), Number(mo), 0, 23, 59, 59, 999);
    }
  }

  if (!start || !end) {
    // Fallback to "current month"
    const now = new Date();
    start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  }

  return rows.filter((row) => {
    const dt = parseDemoDateCell(row.demo_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return dt >= start && dt <= end;
  });
}

/** Client-side filter for /leads when opened with ?period=month (and optional ?month=YYYY-MM). */
function filterLeadsRowsByMonth(rows, monthYYYYMM) {
  if (!rows?.length) return rows;
  let start;
  let end;

  if (monthYYYYMM && typeof monthYYYYMM === "string" && monthYYYYMM.includes("-")) {
    const match = monthYYYYMM.trim().match(/^(\d{4})-(\d{2})$/);
    if (match) {
      const [, y, mo] = match;
      start = new Date(Number(y), Number(mo) - 1, 1, 0, 0, 0, 0);
      end = new Date(Number(y), Number(mo), 0, 23, 59, 59, 999);
    }
  }

  if (!start || !end) {
    // Fallback to "current month"
    const now = new Date();
    start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  }

  return rows.filter((row) => {
    const dt = parseLeadListDateTime(row.lead_datetime);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return dt >= start && dt <= end;
  });
}

/** Open/closed filter — matches dashboard open_leads (stage lead_status, not lead row). */
function filterLeadsRowsByLeadStatus(rows, leadStatusFilter) {
  if (!rows?.length) return rows;

  const trimmedStatus =
    leadStatusFilter != null ? String(leadStatusFilter).trim() : "";
  if (trimmedStatus.toLowerCase() === "all") return rows;

  const q =
    trimmedStatus === ""
      ? "open"
      : String(trimmedStatus).trim().toLowerCase();

  return rows.filter((row) => {
    const status = String(row?.__lead_status_display ?? "")
      .trim()
      .toLowerCase();
    const normalizedStatus = status === "close" ? "closed" : status;
    const normalizedQuery = q === "close" ? "closed" : q;
    return normalizedStatus === normalizedQuery;
  });
}

/**
 * Laravel may return a plain array in `response.data.data`, or a paginator
 * `{ data: [...], current_page, total, ... }`. Unwrap to an array for mapping.
 */
function normalizeListPayload(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  return [];
}

function parseLeadDateFilter(value) {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;

  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));

  m = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Parses admission_date: API usually `YYYY-MM-DD`; also accepts `DD-MM-YYYY`. */
function parseAdmissionDateCell(str) {
  if (!str || str === "-") return null;
  const s = String(str).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, y, mo, d] = iso;
    return new Date(Number(y), Number(mo) - 1, Number(d));
  }
  const dmy = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) {
    const [, d, mo, y] = dmy;
    return new Date(Number(y), Number(mo) - 1, Number(d));
  }
  return null;
}

/** Admissions with admission_date = today (local calendar). */
function filterAdmissionsRowsAdmissionToday(rows) {
  if (!rows?.length) return rows;
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const day = now.getDate();
  return rows.filter((row) => {
    const dt = parseAdmissionDateCell(row.admission_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return (
      dt.getFullYear() === y &&
      dt.getMonth() === m &&
      dt.getDate() === day
    );
  });
}

/** Admissions with admission_date in a month (optional ?month=YYYY-MM, else current month). */
function filterAdmissionsRowsByMonth(rows, monthYYYYMM) {
  if (!rows?.length) return rows;
  let start;
  let end;

  if (monthYYYYMM && typeof monthYYYYMM === "string" && monthYYYYMM.includes("-")) {
    const match = monthYYYYMM.trim().match(/^(\d{4})-(\d{2})$/);
    if (match) {
      const [, y, mo] = match;
      start = new Date(Number(y), Number(mo) - 1, 1, 0, 0, 0, 0);
      end = new Date(Number(y), Number(mo), 0, 23, 59, 59, 999);
    }
  }

  if (!start || !end) {
    const now = new Date();
    start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  }

  return rows.filter((row) => {
    const dt = parseAdmissionDateCell(row.admission_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return dt >= start && dt <= end;
  });
}

/** Fee installments: due_date is shown as `DD-MM-YYYY` (FormatDateV1). */
function filterFeeInstallmentsByDuePeriod(rows, period) {
  if (!rows?.length || !period) return rows;
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart);
  todayEnd.setHours(23, 59, 59, 999);

  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );

  return rows.filter((row) => {
    const dt = parseDemoDateCell(row.due_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    if (period === "today") {
      return dt >= todayStart && dt <= todayEnd;
    }
    if (period === "month") {
      return dt >= monthStart && dt <= monthEnd;
    }
    return true;
  });
}

/** Fee installments due in selected month (`?month=YYYY-MM`), else current month. */
function filterFeeInstallmentsByDueMonth(rows, monthYYYYMM) {
  if (!rows?.length) return rows;
  let start;
  let end;

  if (monthYYYYMM && typeof monthYYYYMM === "string" && monthYYYYMM.includes("-")) {
    const match = monthYYYYMM.trim().match(/^(\d{4})-(\d{2})$/);
    if (match) {
      const [, y, mo] = match;
      start = new Date(Number(y), Number(mo) - 1, 1, 0, 0, 0, 0);
      end = new Date(Number(y), Number(mo), 0, 23, 59, 59, 999);
    }
  }

  if (!start || !end) {
    const now = new Date();
    start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  }

  return rows.filter((row) => {
    const dt = parseDemoDateCell(row.due_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return dt >= start && dt <= end;
  });
}

/** Parses fee installment due date from raw ISO or list cell `DD-MM-YYYY`. */
function parseFeeInstallmentDueDate(row) {
  const raw =
    row?.payment_date_prefill ??
    row?.due_date_raw ??
    row?.due_date ??
    null;
  if (!raw || raw === "-") return null;
  const iso = String(raw).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, y, mo, d] = iso;
    return new Date(Number(y), Number(mo) - 1, Number(d));
  }
  return parseDemoDateCell(row.due_date);
}

function filterFeeInstallmentsOverdue(rows) {
  if (!rows?.length) return rows;
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return rows.filter((row) => {
    const dt = parseFeeInstallmentDueDate(row);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    if (dt >= todayStart) return false;
    const st = String(row.status ?? "")
      .trim()
      .toLowerCase();
    if (st === "paid" || st === "cancelled") return false;
    return true;
  });
}

function hasFeeInstallmentDashboardUrlFilters(searchParams) {
  if (!searchParams) return false;
  return Boolean(
    String(searchParams.get("period") || searchParams.get("due") || "").trim() ||
      String(searchParams.get("due_period") || "").trim() ||
      String(searchParams.get("status") || "").trim()
  );
}

function applyFeeInstallmentRowsForUrl(rows, searchParams) {
  if (!rows?.length || !searchParams) return rows;

  let filtered = rows;
  const period = String(
    searchParams.get("period") || searchParams.get("due") || ""
  )
    .trim()
    .toLowerCase();
  const month = String(searchParams.get("month") || "").trim();
  const status = String(searchParams.get("status") || "").trim().toLowerCase();
  const duePeriod = String(searchParams.get("due_period") || "").trim().toLowerCase();

  if (month) {
    filtered = filterFeeInstallmentsByDueMonth(filtered, month);
  } else if (duePeriod === "this_month") {
    filtered = filterFeeInstallmentsByDueMonth(filtered, null);
  } else if (period === "today" || period === "month") {
    filtered = filterFeeInstallmentsByDuePeriod(filtered, period);
  } else if (period === "overdue") {
    filtered = filterFeeInstallmentsOverdue(filtered);
  }

  if (status === "overdue" || period === "overdue") {
    filtered = filterFeeInstallmentsOverdue(filtered);
  } else if (status === "pending") {
    filtered = filtered.filter(
      (row) => String(row?.status ?? "").trim().toLowerCase() === "pending"
    );
  } else if (status) {
    filtered = filtered.filter(
      (row) => String(row?.status ?? "").trim().toLowerCase() === status
    );
  }

  const parseDueTs = (row) => {
    const raw =
      row?.due_date_raw ?? row?.payment_date_prefill ?? row?.due_date ?? null;
    const ts = raw ? new Date(raw).getTime() : Number.NaN;
    return Number.isFinite(ts) ? ts : Number.POSITIVE_INFINITY;
  };

  // Honor the column header the user actually clicked (sortBy/sortOrder) — this branch
  // previously always reordered by "closest to today" regardless of sortBy, which made
  // clicking any column header (Due Date, Student, Inst.No, ...) appear to do nothing.
  const sortBy = String(searchParams.get("sortBy") || "due_date");
  const dir = searchParams.get("sortOrder") === "desc" ? -1 : 1;
  const compareText = (a, b) =>
    String(a ?? "").localeCompare(String(b ?? ""), undefined, { sensitivity: "base" });
  const compareNumber = (a, b) => (Number(a) || 0) - (Number(b) || 0);

  return [...filtered].sort((a, b) => {
    switch (sortBy) {
      case "student":
        return dir * compareText(a?.student, b?.student);
      case "inst_no":
      case "installment_number":
        return dir * compareNumber(a?.inst_no, b?.inst_no);
      case "fee_inr":
      case "fee_instalment_inr":
        return dir * compareNumber(a?.fee_inr, b?.fee_inr);
      case "fee_curr":
      case "fee_instalment_student_student_currency":
        return dir * compareNumber(a?.fee_curr, b?.fee_curr);
      case "curr_code":
      case "student_currency_code":
        return dir * compareText(a?.curr_code, b?.curr_code);
      case "status":
        return dir * compareText(a?.status, b?.status);
      case "admission":
      case "organization_ems_admission_id":
        return dir * compareText(a?.admission, b?.admission);
      case "due_date":
      default:
        return dir * (parseDueTs(a) - parseDueTs(b));
    }
  });
}

function resolveOrganizationId(userData) {
  return (
    userData?.organization?.organization_id ??
    userData?.organization_id ??
    userData?.user_active_organization?.organization_id ??
    null
  );
}

function resolveOrganizationUserId(userData) {
  return (
    userData?.organization_user_id ??
    userData?.organization_user?.organization_user_id ??
    userData?.user_active_organization?.organization_user_id ??
    null
  );
}

function listUsesDashboardTrainerPhotos(apiEndpoint) {
  const endpoint = String(apiEndpoint || "");
  return (
    endpoint.includes("/api/ems/batch-class-trainers") ||
    endpoint.includes("/api/ems/batch-class-record-trainers") ||
    endpoint.includes("/api/ems/class-records")
  );
}

function CommonList({
  title,
  addButtonLabel,
  apiEndpoint,
  filteredApiEndpoint = "",
  filterModule = "",
  useMetadataFilters = false,
  addRoute,
  editRouteBase,
  mapResponseToRows,
  tablePrimaryKey = "id",
  tableName = "",
  datagridKey = "",
  showAddButton: showAddButtonProp = true,
  showEditButton: showEditButtonProp,
  showViewButton: showViewButtonProp,
  readOnlyList = false,
  hideFilterButton = false,
  hidePersonalizeView = false,
  bypassPermissions = false,
  config = {},
  extraParams = {},
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationData, setPaginationData] = useState(DEFAULT_METADATA_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(Boolean(datagridKey));
  const [leadsSummary, setLeadsSummary] = useState(null);
  const [summaryCardsEnabled, setSummaryCardsEnabled] = useState(null);
  const [trainingProgramsAllOptions, setTrainingProgramsAllOptions] = useState([]);
  const [leadStageOptions, setLeadStageOptions] = useState([]);
  const [feeReceiptStudentOptions, setFeeReceiptStudentOptions] = useState([]);
  const [assessmentTypeOptions, setAssessmentTypeOptions] = useState([]);
  const [batchTrainerBatchOptions, setBatchTrainerBatchOptions] = useState([]);
  const [batchTrainerTrainerOptions, setBatchTrainerTrainerOptions] = useState([]);
  const [contactTimingTimezoneOptions, setContactTimingTimezoneOptions] = useState([]);
  const [offerAudienceOfferOptions, setOfferAudienceOfferOptions] = useState([]);
  const [trainingProgramCategoryOptions, setTrainingProgramCategoryOptions] = useState([]);
  const [trainingProgramMonthOptions, setTrainingProgramMonthOptions] = useState([]);
  const [recruitmentAgencyNameOptions, setRecruitmentAgencyNameOptions] = useState([]);
  const [placementReferralAgencyOptions, setPlacementReferralAgencyOptions] = useState([]);
  const [placementAssistanceAdmissionOptions, setPlacementAssistanceAdmissionOptions] = useState([]);
  const [recruitmentAgencyCountryOptions, setRecruitmentAgencyCountryOptions] = useState([]);
  const [companyIndustryOptions, setCompanyIndustryOptions] = useState([]);
  const [feePortalOpen, setFeePortalOpen] = useState(false);
  const [feePortalLoading, setFeePortalLoading] = useState(false);
  const [feePortalAdmission, setFeePortalAdmission] = useState(null);
  const [batchClassAttendanceDialog, setBatchClassAttendanceDialog] =
    useState({ open: false, batchClassId: null });
  const [studentAttendanceDialog, setStudentAttendanceDialog] = useState({
    open: false,
    studentId: null,
    studentName: "",
    studentCode: "",
  });
  const [studentDetailDialog, setStudentDetailDialog] = useState({
    open: false,
    student: null,
  });

  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const organizationId = resolveOrganizationId(userData);
  const employeePhotoLookupRef = useRef({ key: null, lookup: null, promise: null });
  const trainerPhotoOrgIds = useMemo(() => {
    const ids = new Set();
    if (organizationId) ids.add(String(organizationId));
    const orgLists = [
      userData?.organizations,
      userData?.organization,
      userData?.client?.organization,
      userData?.client?.Organization,
      userData?.Client?.organization,
      userData?.Client?.Organization,
    ];
    orgLists.forEach((list) => {
      const rows = Array.isArray(list) ? list : list ? [list] : [];
      rows.forEach((org) => {
        const id = org?.organization_id;
        if (id != null && String(id).trim() !== "") ids.add(String(id));
      });
    });
    return Array.from(ids);
  }, [organizationId, userData]);

  const attachDashboardTrainerPhotos = useCallback(
    async (rows) => {
      if (!listUsesDashboardTrainerPhotos(apiEndpoint) || !organizationId) {
        return rows;
      }
      const cacheKey = trainerPhotoOrgIds.join(",");
      const cached = employeePhotoLookupRef.current;
      let lookup = cached.key === cacheKey ? cached.lookup : null;
      if (!lookup) {
        if (!(cached.key === cacheKey && cached.promise)) {
          const headers = {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            Accept: "application/json",
          };
          const promise = Promise.all([
            Promise.all(
              trainerPhotoOrgIds.map((orgId) =>
                axios
                  .get(`${MAIN_URL}/api/organizations/${orgId}/filter-employee`, { headers })
                  .then((response) => {
                    let employeesData = Array.isArray(response.data)
                      ? response.data
                      : response.data?.employees || response.data?.data || [];
                    if (
                      Array.isArray(employeesData) &&
                      employeesData.length > 0 &&
                      Array.isArray(employeesData[0])
                    ) {
                      employeesData = employeesData[0];
                    }
                    return Array.isArray(employeesData) ? employeesData : [];
                  })
                  .catch(() => [])
              )
            ),
            axios
              .get(
                `${MAIN_URL}/api/organizations/${organizationId}/organization-level/hierarchy`,
                { headers }
              )
              .then((response) =>
                Array.isArray(response.data?.people) ? response.data.people : []
              )
              .catch(() => []),
          ])
            .then(([employeeGroups, people]) => {
              const employees = employeeGroups.flat();
              const sharedPeople = (people || []).map((person) => ({
                profile_image_url: person?.profile_image_url,
                organization_user_id: person?.organization_user_id,
                Name: person?.name,
                full_name: person?.name,
              }));
              const built = buildEmployeePhotoLookup([...employees, ...sharedPeople]);
              employeePhotoLookupRef.current = { key: cacheKey, lookup: built, promise: null };
              return built;
            })
            .catch(() => {
              const empty = buildEmployeePhotoLookup([]);
              employeePhotoLookupRef.current = { key: cacheKey, lookup: empty, promise: null };
              return empty;
            });
          employeePhotoLookupRef.current = { key: cacheKey, lookup: null, promise };
        }
        lookup = await employeePhotoLookupRef.current.promise;
      }
      return applyEmployeeTrainerPhotos(rows, lookup);
    },
    [apiEndpoint, organizationId, trainerPhotoOrgIds]
  );
  const organizationUserId = resolveOrganizationUserId(userData);
  const org = userData?.organization;
  const navigate = useNavigate();
  const fetchRequestSeqRef = useRef(0);
  const hasInitializedMetadataURL = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [feeInstallmentSearchInput, setFeeInstallmentSearchInput] = useState("");
  const [feeInstallmentSearch, setFeeInstallmentSearch] = useState("");
  const feeInstallmentFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/fee-installments")) return "";
    return [
      String(searchParams.get("period") || searchParams.get("due") || "").trim(),
      String(searchParams.get("due_period") || "").trim(),
      String(searchParams.get("month") || "").trim(),
      String(searchParams.get("status") || "").trim(),
      String(feeInstallmentSearch || "").trim(),
    ].join("|");
  }, [apiEndpoint, searchParams, feeInstallmentSearch]);
  const feeInstallmentDashboardFilters = useMemo(
    () =>
      apiEndpoint?.includes("/api/ems/fee-installments") &&
      hasFeeInstallmentDashboardUrlFilters(searchParams),
    [apiEndpoint, searchParams]
  );
  const studentsFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/students")) return "";
    return [
      String(searchParams.get("admission_period") || "").trim(),
      String(searchParams.get("student_status") || "").trim(),
      String(searchParams.get("batch_status") || "").trim(),
      String(searchParams.get("batch_run_status") || "").trim(),
      String(searchParams.get("training_program") || "").trim(),
      String(searchParams.get("training_program_id") || "").trim(),
    ].join("|");
  }, [apiEndpoint, searchParams]);
  const admissionsFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/admissions")) return "";
    return [
      String(searchParams.get("admission_period") || "").trim(),
      String(searchParams.get("training_program") || "").trim(),
      String(searchParams.get("training_program_id") || "").trim(),
      String(searchParams.get("training_program_category") || "").trim(),
    ].join("|");
  }, [apiEndpoint, searchParams]);
  const placementPoolFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/placement-pool")) return "";
    return [
      String(searchParams.get("training_program") || "").trim(),
      String(searchParams.get("training_program_id") || "").trim(),
      String(searchParams.get("batch_student_status") || "").trim(),
    ].join("|");
  }, [apiEndpoint, searchParams]);
  const offerAudiencesFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/offer-audiences")) return "";
    return [
      String(searchParams.get("organization_ems_offer_id") || "").trim(),
      String(searchParams.get("recipient_type") || "").trim(),
    ].join("|");
  }, [apiEndpoint, searchParams]);
  const offersFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/offers")) return "";
    return [
      String(searchParams.get("training_program") || "").trim(),
      String(searchParams.get("training_program_id") || "").trim(),
    ].join("|");
  }, [apiEndpoint, searchParams]);
  const batchTrainersFetchKey = useMemo(() => {
    if (
      !apiEndpoint?.includes("/api/ems/batch-trainers") &&
      !apiEndpoint?.includes("/api/ems/batch-class-trainers") &&
      !apiEndpoint?.includes("/api/ems/batch-class-record-trainers")
    ) return "";
    return [
      String(searchParams.get("organization_ems_batch_id") || "").trim(),
      String(searchParams.get("trainer_employee_id") || "").trim(),
    ].join("|");
  }, [apiEndpoint, searchParams]);
  const testsFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/tests")) return "";
    return [
      String(searchParams.get("training_program") || "").trim(),
      String(searchParams.get("training_program_id") || "").trim(),
    ].join("|");
  }, [apiEndpoint, searchParams]);
  const assessmentTypesFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/assessment-types")) return "";
    return String(searchParams.get("assessment_delivery_mode") || "").trim();
  }, [apiEndpoint, searchParams]);
  const schedulesFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/assessment-schedules")) return "";
    return [
      String(searchParams.get("organization_ems_batch_id") || "").trim(),
      String(searchParams.get("status") || "").trim(),
    ].join("|");
  }, [apiEndpoint, searchParams]);

  const isSelfScopedTrainerList = useMemo(
    () => isEmsTrainerSelfScopedList(apiEndpoint, Permission, userData),
    [apiEndpoint, Permission, userData]
  );

  const metadataListFetchKey = useMemo(() => {
    if (!useMetadataFilters) return "";
    // Page changes only slice client-side for self-scoped trainer lists.
    if (isSelfScopedTrainerList) {
      const params = new URLSearchParams(searchParams);
      params.delete("page");
      params.delete("pageSize");
      return `self-scope|${params.toString()}`;
    }
    return searchParams.toString();
  }, [useMetadataFilters, searchParams, isSelfScopedTrainerList]);

  const resolvedDatagridKey = datagridKey || "";

  useEffect(() => {
    const isSummaryCardsList =
      apiEndpoint?.includes("/api/ems/fee-installments") ||
      (apiEndpoint?.includes("/api/ems/students") &&
        !apiEndpoint.includes("/api/ems/students-fees")) ||
      (apiEndpoint?.includes("/api/ems/batches") &&
        !apiEndpoint.includes("/api/ems/batch-")) ||
      (apiEndpoint?.includes("/api/ems/leads") &&
        !apiEndpoint.includes("/api/ems/lead-"));

    if (!organizationId || !isSummaryCardsList) {
      setSummaryCardsEnabled(true);
      return undefined;
    }

    let cancelled = false;
    setSummaryCardsEnabled(null);

    fetchInstituteSettings(organizationId)
      .then((response) => {
        if (cancelled) return;
        const savedValue = response?.data?.summary_cards_enabled;
        setSummaryCardsEnabled(savedValue == null ? true : !!savedValue);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Error fetching summary cards setting:", error);
          setSummaryCardsEnabled(true);
        }
      });

    const handleSettingsUpdated = (event) => {
      const detail = event?.detail || {};
      if (Number(detail.organizationId) !== Number(organizationId)) return;
      setSummaryCardsEnabled(detail.summaryCardsEnabled !== false);
    };
    window.addEventListener(
      "ems-institute-settings-updated",
      handleSettingsUpdated
    );

    return () => {
      cancelled = true;
      window.removeEventListener(
        "ems-institute-settings-updated",
        handleSettingsUpdated
      );
    };
  }, [apiEndpoint, organizationId]);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!organizationId || !resolvedDatagridKey) {
        setLoadingConfig(false);
        return;
      }

      try {
        const datagridKey = `${resolvedDatagridKey}_${organizationId}`;
        const requestPayload = {
          datagrid_key: datagridKey,
          organization_id: organizationId,
        };
        if (organizationUserId) {
          requestPayload.organization_user_id = organizationUserId;
        }

        const configRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(requestPayload),
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const serverCfg =
            configResponse?.datagrid?.datagrid_default_configuration;

          if (serverCfg && Array.isArray(serverCfg.columns) && serverCfg.columns.length > 0) {
            const gridKey = String(resolvedDatagridKey || "");
            const isBatchClassGrid =
              gridKey.includes("ems_batch_classes") &&
              !gridKey.includes("ems_batch_class_records");
            let columns = isBatchClassGrid
              ? serverCfg.columns.filter((col) => (col.key || col.field) !== "trainer")
              : [...serverCfg.columns];
            const nextCfg = { ...serverCfg, columns };
            setTableConfig(nextCfg);
            setConfigColumns(nextCfg.columns);
          } else {
            setTableConfig(null);
            setConfigColumns(null);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setTableConfig(null);
        setConfigColumns(null);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [organizationId, organizationUserId, resolvedDatagridKey]);

  useEffect(() => {
    if (!useMetadataFilters) return;
    if (hasInitializedMetadataURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    if (apiEndpoint?.includes("/api/ems/fee-installments")) {
      const legacyDue = String(params.get("due") || "").trim().toLowerCase();
      if (legacyDue && !params.get("period")) {
        params.set("period", legacyDue);
        params.delete("due");
        needsUpdate = true;
      }
      if (
        String(params.get("period") || "").trim().toLowerCase() === "month" &&
        !params.get("due_period")
      ) {
        params.set("due_period", "this_month");
        needsUpdate = true;
      }
      const status = params.get("status");
      if (status && status !== status.toLowerCase()) {
        params.set("status", status.toLowerCase());
        needsUpdate = true;
      }
    }

    const metadataDefaults = resolveMetadataDefaultUrlParams(filterModule, tableName);
    Object.entries(metadataDefaults).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedMetadataURL.current = true;
  }, [useMetadataFilters, filterModule, tableName, apiEndpoint, searchParams, setSearchParams]);

  useEffect(() => {
    if (!apiEndpoint?.includes("/api/ems/fee-installments")) {
      setFeeInstallmentSearch("");
      return;
    }
    const timer = setTimeout(() => {
      setFeeInstallmentSearch(String(feeInstallmentSearchInput || "").trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [apiEndpoint, feeInstallmentSearchInput]);

  const basePath = addRoute || (editRouteBase || "").replace(/\/edit$/, "") || "";
  const actions = useMemo(() => getModuleActions(basePath), [basePath]);

  const hasPermission = (action) => checkPermission(Permission, action);

  const editDeleteActions = useMemo(() => {
    const edit = actions.edit || null;
    const del = actions.delete || null;
    if (!edit && !del) return undefined;
    return [edit, del];
  }, [actions.edit, actions.delete]);

  const showAddButton =
    !readOnlyList &&
    showAddButtonProp &&
    (bypassPermissions || (actions.add ? hasPermission(actions.add) : true));
  const showViewButton = readOnlyList
    ? false
    : showViewButtonProp === false
      ? false
      : bypassPermissions
        ? true
      : actions.view
        ? hasPermission(actions.view) ||
          (actions.list ? hasPermission(actions.list) : false)
        : actions.list
          ? hasPermission(actions.list)
          : showViewButtonProp === true
            ? true
            : true;
  const showEditButton = readOnlyList
    ? false
    : showEditButtonProp === false
      ? false
      : bypassPermissions
        ? true
      : actions.edit
        ? hasPermission(actions.edit)
        : true;
  const showDeleteButton = readOnlyList
    ? false
    : bypassPermissions
      ? true
    : actions.delete
      ? hasPermission(actions.delete)
      : false;

  const isFeeInstallmentsList = basePath === "/fee-installments";
  const canAddStudentFee = hasPermission("ADD_STUDENT_FEE");
  const showReceiptButton =
    isFeeInstallmentsList ? showViewButton && canAddStudentFee : false;

  const canAddStudent = hasPermission("ADD_STUDENT");
  const canAddLeadActivity = hasPermission("ADD_LEAD_ACTIVITY");
  const canAddAdmission = hasPermission("ADD_ADMISSION");
  const canAddDemoParticipant = hasPermission("ADD_DEMO_SESSION");

  const handleReceipt = (row) => {
    // Prefill Fee Receipts form using query parameters.
    // Maps fee installment row fields -> fee receipt form fields.
    const params = new URLSearchParams();

    const studentId = row?.organization_ems_student_id;
    const admissionId = row?.organization_ems_admission_id;
    const feeInstallmentId =
      row?.organization_ems_fee_installment_id ?? row?.id;
    const studentCurrencyCode =
      row?.student_currency_code ?? row?.currency_code ?? row?.currency_code_inr;

    const setParamIfValid = (key, value) => {
      if (value === undefined || value === null) return;
      const s = String(value).trim();
      if (s === "" || s === "-") return;
      params.set(key, s);
    };

    if (studentId && studentId !== "-") {
      params.set("organization_ems_student_id", studentId);
    }
    if (admissionId && admissionId !== "-") {
      params.set("organization_ems_admission_id", admissionId);
    }
    if (feeInstallmentId && feeInstallmentId !== "-") {
      params.set("organization_ems_fee_installment_id", feeInstallmentId);
    }
    setParamIfValid("student_currency_code", studentCurrencyCode);
    // fee_instalment_inr -> amount_received_inr
    setParamIfValid(
      "amount_received_inr",
      row?.fee_instalment_inr ?? row?.fee_installment_inr
    );
    // fee_instalment_student_student_currency -> amount_paid_student_currency
    setParamIfValid(
      "amount_paid_student_currency",
      row?.fee_instalment_student_student_currency
    );
    setParamIfValid("payment_date", row?.payment_date_prefill);

    params.set("from_fee_installment", "1");

    const queryStr = params.toString();
    navigate(`/students-fees/add${queryStr ? `?${queryStr}` : ""}`);
  };

  const isLeadsList = basePath === "/leads";
  const isLeadActivityReport = basePath === "/lead-activity-report";
  const showEnrolledButton = isLeadsList ? showViewButton && canAddStudent : false;
  const canViewLeadActivity =
    hasPermission("VIEW_LEAD_ACTIVITY") || canAddLeadActivity;
  const showLeadActivityButton = isLeadActivityReport
    ? canAddLeadActivity
    : isLeadsList
      ? showViewButton && canAddLeadActivity
      : false;
  const showSnapshotButton = isLeadActivityReport
    ? true
    : isLeadsList
      ? showViewButton && canViewLeadActivity
      : false;

  const handleLeadActivity = (row) => {
    const params = new URLSearchParams();
    const leadId =
      row?.organization_ems_lead_id ?? row?.id ?? row?.lead_id;
    if (
      leadId !== undefined &&
      leadId !== null &&
      String(leadId).trim() !== "" &&
      String(leadId) !== "-"
    ) {
      params.set("organization_ems_lead_id", String(leadId));
    }
    params.set("from_leads_list", "1");
    const q = params.toString();
    navigate(`/lead-activities/add${q ? `?${q}` : ""}`);
  };

  const handleEnrolled = (row) => {
    const blockMessage = getLeadEnrollmentBlockMessage(row);
    if (blockMessage) {
      toast.error(blockMessage);
      return;
    }

    const params = new URLSearchParams();

    // Split full name -> first_name / last_name
    const fullName = String(row?.person_full_name ?? "").trim();
    const parts = fullName ? fullName.split(/\s+/).filter(Boolean) : [];
    const firstName = parts[0] || "";
    const lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";

    if (firstName) params.set("first_name", firstName);
    if (lastName) params.set("last_name", lastName);

    // Address/location ids (should be present after we extend leads mapping)
    const countryId = row?.country_id;
    const stateId = row?.state_id;
    const cityId = row?.city_id;
    const isValidId = (v) => {
      if (v === undefined || v === null) return false;
      const s = String(v).trim();
      return s !== "" && s !== "-";
    };

    if (isValidId(countryId)) params.set("country_id", countryId);
    if (isValidId(stateId)) params.set("state_id", stateId);
    if (isValidId(cityId)) params.set("city_id", cityId);

    // Names fallback: avoids mismatched ids / slow dropdown mismatch.
    const stateName = row?.state_name;
    const cityName = row?.city_name;
    const isValidStr = (v) => {
      if (v === undefined || v === null) return false;
      const s = String(v).trim();
      return s !== "" && s !== "-";
    };

    if (isValidStr(stateName)) params.set("state_name", stateName);
    if (isValidStr(cityName)) params.set("city_name", cityName);

    // Contact fields
    const phone = row?.phone;
    const email = row?.email;
    const alternatePhone = row?.alternate_phone;
    if (phone && phone !== "-") params.set("phone", phone);
    if (email && email !== "-") params.set("email", email);
    if (alternatePhone && alternatePhone !== "-")
      params.set("alternate_phone", alternatePhone);

    // Optional fields
    const address = row?.address;
    const remarks = row?.remarks ?? row?.interested_program_remarks;
    if (address && address !== "-") params.set("address", address);
    if (remarks && remarks !== "-") params.set("remarks", remarks);

    const leadRowId =
      row?.organization_ems_lead_id ?? row?.id ?? row?.lead_id;
    if (
      leadRowId !== undefined &&
      leadRowId !== null &&
      String(leadRowId).trim() !== "" &&
      String(leadRowId) !== "-"
    ) {
      params.set("organization_ems_lead_id", String(leadRowId));
    }

    params.set("from_lead", "1");

    navigate(`/students/add${params.toString() ? `?${params.toString()}` : ""}`);
  };

  const isTrainingProgramsList = basePath === "/training-programs";
  const isStudentsList = basePath === "/students";
  const isAdmissionsList = basePath === "/admissions";
  const showFeePortalButton = isAdmissionsList && showViewButton;
  const showStudentAttendanceButton = isAdmissionsList && showViewButton;
  const showAdmissionButton =
    isStudentsList ? showViewButton && canAddAdmission : false;
  const isDemoSessionsList = basePath === "/demo-sessions";
  const isDemoParticipantsList = basePath === "/demo-participants";
  const isOffersList = basePath === "/offers";
  const isAssessmentTypesList = basePath === "/assessment-types";
  const isOfferAudiencesList = basePath === "/offer-audiences";
  const isFeeReceiptsList = basePath === "/students-fees";
  const isBatchTrainersList = basePath === "/batch-trainers";
  const isBatchClassTrainersList = basePath === "/batch-class-trainers";
  const isBatchClassRecordTrainersList = basePath === "/batch-class-record-trainers";
  const isAssessmentsList = basePath === "/assesments";
  const isTestsList = basePath === "/assessment-tests";
  const isAssessmentSchedulesList = basePath === "/assessment-schedules";
  const isAssessmentResultsList = basePath === "/assesment-results";
  const isTrainingProgramModulesList = basePath === "/training-program-modules";
  const isModuleQuestionsList = basePath === "/module-questions";
  const isCertificatesList = basePath === "/certificates";
  const isStudentReviewsList = basePath === "/student-reviews";
  const isLeadActivitiesList = basePath === "/lead-activities";
  const isLeadContactTimingsList = basePath === "/lead-contact-timings";
  const isLeadStagesList = basePath === "/lead-stages";
  const isRecruitmentAgenciesList = basePath === "/recruitment-agencies";
  const isCompaniesList = basePath === "/companies";
  const isPlacementPoolList = basePath === "/placement-pool";
  const isMockInterviewsList = basePath === "/placement-mock-interviews";
  const isPlacementReferralsList = basePath === "/placement-referrals";
  const isPlacementAssistanceList = basePath === "/placement-assistance";
  const canViewSelfDemoSessions = hasPermission("VIEW_SELF_DEMO_SESSIONS");
  const canViewAllDemoSessions = hasPermission("VIEW_DEMO_SESSIONS");
  const canViewSelfDemoParticipants = hasPermission("VIEW_SELF_DEMO_PARTICIPANTS");
  const canViewAllDemoParticipants = hasPermission("VIEW_DEMO_PARTICIPANTS");
  const canViewSelfBatches = hasPermission("VIEW_SELF_BATCHES");
  const canViewAllBatches = hasPermission("VIEW_BATCHES");
  const canViewSelfBatchStudents = hasPermission("VIEW_SELF_BATCH_STUDENTS");
  const canViewAllBatchStudents = hasPermission("VIEW_BATCH_STUDENTS");
  const canViewSelfBatchClasses = hasPermission("VIEW_SELF_BATCH_CLASSES");
  const canViewAllBatchClasses = hasPermission("VIEW_BATCH_CLASS");
  const canViewSelfBatchClassRecords = hasPermission("VIEW_SELF_BATCH_CLASS_RECORDS");
  const canViewAllBatchClassRecords = hasPermission("VIEW_BATCH_CLASS_RECORD");
  const canViewSelfClassAttendance = hasPermission("VIEW_SELF_CLASS_ATTENDANCE");
  const canViewAllClassAttendance = hasPermission("VIEW_CLASS_ATTENDANCES");
  const effectiveShowViewButton =
    isDemoSessionsList && canViewSelfDemoSessions
      ? showViewButton || canViewSelfDemoSessions
      : showViewButton;
  const showDemoParticipantButton =
    isDemoSessionsList ? effectiveShowViewButton && canAddDemoParticipant : false;
  const isBatchesList = basePath === "/batches";
  const isBatchClassesList = basePath === "/batch-classes";
  const isClassRecordsList = basePath === "/class-records";
  const canMarkClassAttendance =
    hasPermission("VIEW_SELF_CLASS_ATTENDANCE") ||
    hasPermission("VIEW_CLASS_ATTENDANCES") ||
    hasPermission("ADD_CLASS_ATTENDANCE") ||
    hasPermission("EDIT_CLASS_ATTENDANCE");
  const showBatchAttendanceButton =
    (isClassRecordsList || isBatchClassesList) &&
    showViewButton &&
    canMarkClassAttendance;
  const canViewBatches = hasPermission("VIEW_BATCHES") || hasPermission("VIEW_SELF_BATCHES");
  const showBatchClassesButton =
    isBatchesList &&
    showViewButton &&
    canViewBatches;
  const canViewClassRecords =
    hasPermission("VIEW_BATCH_CLASS_RECORD") ||
    hasPermission("VIEW_SELF_BATCH_CLASS_RECORDS") ||
    hasPermission("VIEW_BATCH_CLASS") ||
    hasPermission("ADD_BATCH_CLASS") ||
    hasPermission("EDIT_BATCH_CLASS");
  const showBatchRecordsButton =
    isBatchClassesList &&
    showViewButton &&
    canViewClassRecords;
  const canViewBatchStudents =
    hasPermission("VIEW_BATCH_STUDENTS") ||
    hasPermission("VIEW_SELF_BATCH_STUDENTS") ||
    hasPermission("ADD_BATCH_STUDENT") ||
    hasPermission("EDIT_BATCH_STUDENTS");
  const showBatchStudentsButton =
    isBatchesList &&
    showViewButton &&
    canViewBatchStudents;

  const handleAdmission = (row) => {
    const hasLatestAdmissionDate =
      row?.latest_admission_date != null &&
      String(row.latest_admission_date).trim() !== "" &&
      String(row.latest_admission_date).trim() !== "-";
    const hasAnyAdmissions =
      Array.isArray(row?.student_admissions) && row.student_admissions.length > 0;

    if (hasLatestAdmissionDate || hasAnyAdmissions) {
      toast.error("Student is already enrolled.");
      return;
    }

    const params = new URLSearchParams();

    const studentId = row?.id ?? row?.organization_ems_student_id;
    const leadId =
      row?.organization_ems_lead_id ??
      row?.lead_id ??
      row?.organization_ems_lead_id;
    const trainingProgramId =
      row?.training_program_id ??
      row?.organization_ems_training_program_id ??
      row?.training_program?.organization_ems_training_program_id;

    if (studentId !== undefined && studentId !== null && String(studentId).trim() !== "" && String(studentId) !== "-") {
      params.set("organization_ems_student_id", studentId);
    }
    if (leadId !== undefined && leadId !== null && String(leadId).trim() !== "" && String(leadId) !== "-") {
      params.set("organization_ems_lead_id", leadId);
    }
    if (trainingProgramId !== undefined && trainingProgramId !== null && String(trainingProgramId).trim() !== "" && String(trainingProgramId) !== "-") {
      params.set("training_program_id", trainingProgramId);
    }

    params.set("from_student", "1");

    navigate(`/admissions/add${params.toString() ? `?${params.toString()}` : ""}`);
  };

  const handleFeePortal = async (row) => {
    const id = row?.id ?? row?.organization_ems_admission_id;
    if (
      id === undefined ||
      id === null ||
      String(id).trim() === "" ||
      String(id) === "-"
    ) {
      return;
    }
    setFeePortalOpen(true);
    setFeePortalLoading(true);
    setFeePortalAdmission(null);
    try {
      const res = await axios.get(`${MAIN_URL}/api/ems/admissions/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          organization_id: org?.organization_id,
        },
      });
      const payload = res.data?.data ?? res.data;
      setFeePortalAdmission(payload);
    } catch (err) {
      console.error("Fee portal fetch failed:", err);
      toast.error("Could not load fee details.");
      setFeePortalOpen(false);
    } finally {
      setFeePortalLoading(false);
    }
  };

  const handleStudentNameClick = useCallback((row) => {
    if (!row) return;
    setStudentDetailDialog({ open: true, student: row });
  }, []);

  const handleTrainingProgramNameClick = useCallback(
    (row) => {
      if (!row) return;
      const id =
        row?.organization_ems_training_program_id ??
        row?.id ??
        row?.training_program_id;
      if (
        id === undefined ||
        id === null ||
        String(id).trim() === "" ||
        String(id) === "-"
      ) {
        return;
      }
      navigate(
        `/training-portal?program=${encodeURIComponent(String(id))}`
      );
    },
    [navigate]
  );

  const handleStudentAttendance = (row) => {
    const sid =
      row?.organization_ems_student_id ??
      row?.student?.organization_ems_student_id ??
      row?.student_obj?.organization_ems_student_id;
    if (
      sid === undefined ||
      sid === null ||
      String(sid).trim() === "" ||
      String(sid) === "-"
    ) {
      toast.error("Student not found for this admission.");
      return;
    }
    setStudentAttendanceDialog({
      open: true,
      studentId: sid,
      studentName:
        row?.student_name ||
        [row?.student?.first_name, row?.student?.middle_name, row?.student?.last_name]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        "",
      studentCode:
        row?.student_id_code ||
        row?.student?.student_id ||
        "",
    });
  };

  const handleBatchAttendanceMarking = (row) => {
    const id =
      row?.organization_ems_batch_class_id ??
      row?.batch_class?.organization_ems_batch_class_id ??
      row?.batchClass?.organization_ems_batch_class_id ??
      row?.id;
    if (
      id === undefined ||
      id === null ||
      String(id).trim() === "" ||
      String(id) === "-"
    ) {
      return;
    }

    // Batch Classes list: view topic + student attendance in a dialog.
    if (isBatchClassesList) {
      setBatchClassAttendanceDialog({
        open: true,
        batchClassId: id,
      });
      return;
    }

    const classRecordId =
      row?.organization_ems_batch_class_record_id ??
      row?.organization_ems_class_record_id ??
      row?.batch_record_id ??
      row?.id;
    const params = new URLSearchParams();
    if (
      isClassRecordsList &&
      classRecordId !== undefined &&
      classRecordId !== null &&
      String(classRecordId).trim() !== "" &&
      String(classRecordId) !== "-"
    ) {
      params.set("class_record_id", String(classRecordId));
    }
    navigate(
      `/batch-classes/attendance/${id}${
        params.toString() ? `?${params.toString()}` : ""
      }`
    );
  };
  const handleBatchClassesFromBatch = (row) => {
    const batchId =
      row?.organization_ems_batch_id ??
      row?.batch?.organization_ems_batch_id ??
      row?.id;
    if (
      batchId === undefined ||
      batchId === null ||
      String(batchId).trim() === "" ||
      String(batchId) === "-"
    ) {
      navigate("/batch-classes/add");
      return;
    }
    const params = new URLSearchParams();
    params.set("organization_ems_batch_id", String(batchId));
    navigate(`/batch-classes/add?${params.toString()}`);
  };
  const handleBatchRecordsFromBatchClass = (row) => {
    const batchId =
      row?.organization_ems_batch_id ??
      row?.batch?.organization_ems_batch_id ??
      "";
    const batchClassId =
      row?.organization_ems_batch_class_id ??
      row?.batch_class?.organization_ems_batch_class_id ??
      row?.batchClass?.organization_ems_batch_class_id ??
      row?.id ??
      "";
    const params = new URLSearchParams();
    if (
      batchId !== undefined &&
      batchId !== null &&
      String(batchId).trim() !== "" &&
      String(batchId) !== "-"
    ) {
      params.set("organization_ems_batch_id", String(batchId));
    }
    if (
      batchClassId !== undefined &&
      batchClassId !== null &&
      String(batchClassId).trim() !== "" &&
      String(batchClassId) !== "-"
    ) {
      params.set("organization_ems_batch_class_id", String(batchClassId));
    }
    navigate(`/class-records/add${params.toString() ? `?${params.toString()}` : ""}`);
  };
  const handleBatchStudentsFromBatch = (row) => {
    const batchId =
      row?.organization_ems_batch_id ??
      row?.batch?.organization_ems_batch_id ??
      row?.id;
    if (
      batchId === undefined ||
      batchId === null ||
      String(batchId).trim() === "" ||
      String(batchId) === "-"
    ) {
      navigate("/batch-students/add");
      return;
    }
    const params = new URLSearchParams();
    params.set("organization_ems_batch_id", String(batchId));
    navigate(`/batch-students/add?${params.toString()}`);
  };

  const handleDemoParticipant = (row) => {
    const params = new URLSearchParams();
    const demoSessionId =
      row?.organization_ems_demo_session_id ?? row?.id;
    if (
      demoSessionId !== undefined &&
      demoSessionId !== null &&
      String(demoSessionId).trim() !== "" &&
      String(demoSessionId) !== "-"
    ) {
      params.set("organization_ems_demo_session_id", demoSessionId);
    }
    navigate(
      `/demo-participants/add${params.toString() ? `?${params.toString()}` : ""}`
    );
  };

  const handleMetadataSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleMetadataPageChange = useCallback(
    (page, nextPageSize) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(page));
        const resolvedPageSize =
          nextPageSize ??
          (Number.parseInt(prev.get("pageSize"), 10) ||
            Number.parseInt(next.get("pageSize"), 10) ||
            25);
        next.set("pageSize", String(resolvedPageSize));
        return next;
      });
    },
    [setSearchParams]
  );

  // ✅ Fetch data (generic)
  const fetchData = async (loadingVal = true) => {
    if (!apiEndpoint) return;
    const requestSeq = ++fetchRequestSeqRef.current;
    setLoading(loadingVal);
    try {
      if (
        useMetadataFilters &&
        filteredApiEndpoint &&
        organizationId
      ) {
        const filters = buildMetadataFiltersFromSearchParams(
          searchParams,
          filterModule || tableName
        );
        const search = searchParams.get("search");
        const defaultSortField = resolveMetadataDefaultSortField(
          filterModule,
          tableName
        );
        const sortByFromUrl = searchParams.get("sortBy") || defaultSortField;
        const resolvedSortField = resolveMetadataSortField(
          filterModule || tableName,
          sortByFromUrl
        );
        const sortDirection =
          searchParams.get("sortOrder") ||
          resolveMetadataDefaultUrlParams(filterModule, tableName).sortOrder ||
          "desc";
        const selfScopedFetch = isEmsTrainerSelfScopedList(
          apiEndpoint,
          Permission,
          userData
        );
        const requestBody = {
          filters,
          page: selfScopedFetch
            ? 1
            : Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: selfScopedFetch
            ? SELF_SCOPED_FETCH_PAGE_SIZE
            : Number.parseInt(searchParams.get("pageSize"), 10) || 25,
          organization_id: organizationId,
          ...(search && search.trim() !== "" ? { search: search.trim() } : {}),
        };

        if (sortByFromUrl) {
          requestBody.sort = {
            field: sortByFromUrl,
            direction: sortDirection,
          };
          requestBody.sortBy = sortByFromUrl;
          requestBody.sortOrder = sortDirection;
        }

        if (filterModule === "ems_leads" || tableName === "ems_leads") {
          Object.assign(requestBody, {
            training_program: searchParams.get("training_program"),
            lead_stage: searchParams.get("lead_stage"),
            lead_status: searchParams.has("lead_status")
              ? searchParams.get("lead_status")
              : "open",
            period: searchParams.get("period"),
            month: searchParams.get("month"),
            lead_date: searchParams.get("lead_date"),
          });
        }

        if (filterModule === "ems_demo_sessions" || tableName === "ems_demo_sessions") {
          Object.assign(requestBody, {
            period: searchParams.get("period"),
            month: searchParams.get("month"),
            demo_date: searchParams.get("demo_date"),
            training_program:
              searchParams.get("training_program") ||
              searchParams.get("training_program_id"),
            mode: searchParams.get("mode") || searchParams.get("demo_mode"),
          });
        }

        if (filterModule === "ems_students" || tableName === "ems_students") {
          Object.assign(requestBody, {
            admission_period: searchParams.get("admission_period"),
            training_program:
              searchParams.get("training_program") ||
              searchParams.get("training_program_id"),
            training_program_id: searchParams.get("training_program_id"),
            student_status: searchParams.get("student_status"),
            batch_status: searchParams.get("batch_status"),
            batch_run_status: searchParams.get("batch_run_status"),
          });
        }

        if (filterModule === "ems_admissions" || tableName === "ems_admissions") {
          Object.assign(requestBody, {
            admission_period: searchParams.get("admission_period"),
            training_program: searchParams.get("training_program"),
            training_program_id: searchParams.get("training_program_id"),
            training_program_category: searchParams.get("training_program_category"),
            period: searchParams.get("period"),
            month: searchParams.get("month"),
            enrolled: searchParams.get("enrolled"),
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
          });
        }

        if (
          filterModule === "ems_fee_installments" ||
          tableName === "ems_fee_installments"
        ) {
          Object.assign(requestBody, {
            period:
              searchParams.get("period") || searchParams.get("due"),
            month: searchParams.get("month"),
            status: searchParams.get("status"),
            due_period: searchParams.get("due_period"),
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            organization_ems_fee_installment_id: searchParams.get(
              "organization_ems_fee_installment_id"
            ),
          });
          const feeSearch = String(feeInstallmentSearch || "").trim();
          if (feeSearch) {
            requestBody.search = feeSearch;
          }
        }

        if (
          filterModule === "ems_student_fees" ||
          tableName === "ems_student_fees"
        ) {
          Object.assign(requestBody, {
            period: searchParams.get("period"),
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            organization_ems_fee_installment_id: searchParams.get(
              "organization_ems_fee_installment_id"
            ),
            payment_mode: searchParams.get("payment_mode"),
          });
        }

        if (filterModule === "ems_batches" || tableName === "ems_batches") {
          Object.assign(requestBody, {
            training_program: searchParams.get("training_program"),
            status: searchParams.get("status"),
            mode: searchParams.get("mode"),
            period: searchParams.get("period"),
          });
        }

        if (
          filterModule === "ems_batch_students" ||
          tableName === "ems_batch_students"
        ) {
          Object.assign(requestBody, {
            organization_ems_batch_id: searchParams.get("organization_ems_batch_id"),
            training_program: searchParams.get("training_program"),
            batch_status: searchParams.has("batch_status")
              ? searchParams.get("batch_status")
              : "all",
            batch_run_status: searchParams.get("batch_run_status"),
            mode: searchParams.get("mode"),
          });
        }

        if (
          filterModule === "ems_batch_classes" ||
          tableName === "ems_batch_classes"
        ) {
          Object.assign(requestBody, {
            organization_ems_batch_id: searchParams.get("organization_ems_batch_id"),
            training_program: searchParams.get("training_program"),
            class_status: searchParams.get("class_status") || searchParams.get("status"),
            mode: searchParams.get("mode"),
            batch_activity: searchParams.get("batch_activity") || "active",
          });
        }

        if (
          filterModule === "ems_batch_class_records" ||
          tableName === "ems_batch_class_records"
        ) {
          Object.assign(requestBody, {
            organization_ems_batch_id: searchParams.get("organization_ems_batch_id"),
            organization_ems_batch_class_id: searchParams.get(
              "organization_ems_batch_class_id"
            ),
            training_program: searchParams.get("training_program"),
            status: searchParams.get("status"),
            mode: searchParams.get("mode"),
          });
        }

        if (
          filterModule === "ems_class_attendance" ||
          tableName === "ems_class_attendance"
        ) {
          Object.assign(requestBody, {
            organization_ems_batch_class_id: searchParams.get(
              "organization_ems_batch_class_id"
            ),
            organization_ems_batch_class_record_id:
              searchParams.get("organization_ems_batch_class_record_id") ||
              searchParams.get("class_record_id"),
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            attendance_status: searchParams.get("attendance_status"),
          });
        }

        if (
          filterModule === "ems_assessments" ||
          tableName === "ems_assessments"
        ) {
          Object.assign(requestBody, {
            training_program: searchParams.get("training_program"),
            training_program_id: searchParams.get("training_program_id"),
            organization_ems_batch_id: searchParams.get(
              "organization_ems_batch_id"
            ),
            organization_ems_assessment_type_id: searchParams.get(
              "organization_ems_assessment_type_id"
            ),
            assessment_type: searchParams.get("assessment_type"),
            status: searchParams.get("status"),
          });
        }

        if (
          filterModule === "ems_assessment_results" ||
          tableName === "ems_assessment_results"
        ) {
          Object.assign(requestBody, {
            organization_ems_assessment_id: searchParams.get(
              "organization_ems_assessment_id"
            ),
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            result_status: searchParams.get("result_status"),
          });
        }

        if (
          filterModule === "ems_training_program_modules" ||
          tableName === "ems_training_program_modules"
        ) {
          Object.assign(requestBody, {
            organization_ems_training_program_id: searchParams.get(
              "organization_ems_training_program_id"
            ),
            training_program: searchParams.get("training_program"),
            training_program_id: searchParams.get("training_program_id"),
          });
        }

        if (
          filterModule === "ems_module_questions" ||
          tableName === "ems_module_questions"
        ) {
          Object.assign(requestBody, {
            organization_ems_training_program_id: searchParams.get(
              "organization_ems_training_program_id"
            ),
            training_program: searchParams.get("training_program"),
            training_program_id: searchParams.get("training_program_id"),
          });
        }

        if (
          filterModule === "ems_training_programs" ||
          tableName === "ems_training_programs"
        ) {
          Object.assign(requestBody, {
            organization_ems_training_program_category_id: searchParams.get(
              "organization_ems_training_program_category_id"
            ),
            training_program_category_id: searchParams.get(
              "training_program_category_id"
            ),
            training_program: searchParams.get("training_program"),
            training_program_id: searchParams.get("training_program_id"),
            is_active: searchParams.get("is_active"),
            duration_in_months: searchParams.get("duration_in_months"),
          });
        }

        if (
          filterModule === "ems_certificates" ||
          tableName === "ems_certificates"
        ) {
          Object.assign(requestBody, {
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            organization_ems_batch_id: searchParams.get(
              "organization_ems_batch_id"
            ),
            training_program: searchParams.get("training_program"),
            training_program_id: searchParams.get("training_program_id"),
            certificate_status: searchParams.get("certificate_status"),
          });
        }

        if (
          filterModule === "ems_recruitment_agencies" ||
          tableName === "ems_recruitment_agencies"
        ) {
          Object.assign(requestBody, {
            status: searchParams.get("status"),
            country_id: searchParams.get("country_id"),
            country: searchParams.get("country"),
            agency_name: searchParams.get("agency_name"),
            state_id: searchParams.get("state_id"),
            city_id: searchParams.get("city_id"),
          });
        }

        if (
          filterModule === "ems_companies" ||
          tableName === "ems_companies"
        ) {
          Object.assign(requestBody, {
            status: searchParams.get("status"),
            industry: searchParams.get("industry"),
          });
        }

        if (
          filterModule === "ems_placement_assistance" ||
          tableName === "ems_placement_assistance"
        ) {
          Object.assign(requestBody, {
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            overall_status: searchParams.get("overall_status"),
          });
        }

        if (
          filterModule === "ems_placement_mock_interviews" ||
          tableName === "ems_placement_mock_interviews"
        ) {
          Object.assign(requestBody, {
            interview_type: searchParams.get("interview_type"),
            result: searchParams.get("result"),
          });
        }

        if (
          filterModule === "ems_placement_referrals" ||
          tableName === "ems_placement_referrals"
        ) {
          Object.assign(requestBody, {
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            training_program: searchParams.get("training_program"),
            training_program_id: searchParams.get("training_program_id"),
            organization_ems_recruitment_agency_id: searchParams.get(
              "organization_ems_recruitment_agency_id"
            ),
            organization_ems_company_id: searchParams.get(
              "organization_ems_company_id"
            ),
            referral_status: searchParams.get("referral_status"),
          });
        }

        if (
          filterModule === "ems_student_reviews" ||
          tableName === "ems_student_reviews"
        ) {
          Object.assign(requestBody, {
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            organization_ems_student_review_platform_id: searchParams.get(
              "organization_ems_student_review_platform_id"
            ),
            review_status: searchParams.get("review_status"),
          });
        }

        if (
          filterModule === "ems_lead_activities" ||
          tableName === "ems_lead_activities"
        ) {
          Object.assign(requestBody, {
            lead_direction: searchParams.get("lead_direction"),
            status: searchParams.get("status"),
          });
        }

        if (
          filterModule === "ems_lead_contact_timings" ||
          tableName === "ems_lead_contact_timings"
        ) {
          Object.assign(requestBody, {
            preferred_contact_mode: searchParams.get("preferred_contact_mode"),
            preferred_contact_timezone: searchParams.get("preferred_contact_timezone"),
          });
        }

        if (
          filterModule === "ems_lead_stages" ||
          tableName === "ems_lead_stages"
        ) {
          Object.assign(requestBody, {
            lead_status: searchParams.get("lead_status"),
          });
        }

        if (
          filterModule === "ems_training_spaces" ||
          tableName === "ems_training_spaces"
        ) {
          Object.assign(requestBody, {
            mode: searchParams.get("mode"),
            status: searchParams.get("status"),
          });
        }

        const leadIdParam = searchParams.get("organization_ems_lead_id");
        const isLeadScopedMetadataList =
          filterModule === "ems_lead_activities" ||
          tableName === "ems_lead_activities" ||
          filterModule === "ems_lead_contact_timings" ||
          tableName === "ems_lead_contact_timings" ||
          filterModule === "ems_demo_participants" ||
          tableName === "ems_demo_participants";
        if (
          isLeadScopedMetadataList &&
          leadIdParam &&
          String(leadIdParam).trim() !== ""
        ) {
          requestBody.organization_ems_lead_id = String(leadIdParam).trim();
        }

        const demoSessionIdParam = searchParams.get("organization_ems_demo_session_id");
        const isDemoSessionScopedMetadataList =
          filterModule === "ems_demo_participants" ||
          tableName === "ems_demo_participants";
        if (isDemoSessionScopedMetadataList) {
          Object.assign(requestBody, {
            training_program:
              searchParams.get("training_program") ||
              searchParams.get("training_program_id"),
            attendance_status: searchParams.get("attendance_status"),
          });
          if (demoSessionIdParam && String(demoSessionIdParam).trim() !== "") {
            requestBody.organization_ems_demo_session_id =
              String(demoSessionIdParam).trim();
          }
        }

        const postFilteredList = async (body) => {
          const response = await fetch(`${MAIN_URL}${filteredApiEndpoint}`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
              "X-Application-Key": "ems",
            },
            body: JSON.stringify(body),
          });
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        };

        let result;
        let rawRows = [];

        if (selfScopedFetch) {
          // Metadata engine rejects limit > 100 — page through and merge for correct totals.
          let page = 1;
          let reportedTotal = null;
          while (page <= SELF_SCOPED_FETCH_MAX_PAGES) {
            if (requestSeq !== fetchRequestSeqRef.current) return;
            const pageResult = await postFilteredList({
              ...requestBody,
              page,
              pageSize: SELF_SCOPED_FETCH_PAGE_SIZE,
            });
            if (pageResult?.success === false) {
              setData([]);
              setPaginationData(DEFAULT_METADATA_PAGINATION);
              console.error(
                `Error fetching ${title}:`,
                pageResult?.message || pageResult?.error
              );
              return;
            }
            const pageRows = Array.isArray(pageResult?.data) ? pageResult.data : [];
            rawRows = rawRows.concat(pageRows);
            reportedTotal =
              pageResult?.pagination?.total != null
                ? Number(pageResult.pagination.total)
                : reportedTotal;
            const reachedReportedTotal =
              reportedTotal != null && rawRows.length >= reportedTotal;
            const shortPage = pageRows.length < SELF_SCOPED_FETCH_PAGE_SIZE;
            if (shortPage || reachedReportedTotal || pageRows.length === 0) {
              result = pageResult;
              break;
            }
            page += 1;
          }
          if (!result) {
            result = { success: true, data: rawRows };
          }
        } else {
          result = await postFilteredList(requestBody);
        }

        if (requestSeq !== fetchRequestSeqRef.current) return;

        if (result?.success === false) {
          setData([]);
          setPaginationData(DEFAULT_METADATA_PAGINATION);
          console.error(`Error fetching ${title}:`, result?.message || result?.error);
          return;
        }
        if (!selfScopedFetch) {
          rawRows = result?.data ?? [];
        }
        const mappedData = await attachDashboardTrainerPhotos(
          mapResponseToRows
            ? mapResponseToRows(Array.isArray(rawRows) ? rawRows : [])
            : rawRows
        );
        const fullySortedData = sortComputedMetadataRows(
          Array.isArray(mappedData) ? mappedData : [],
          resolvedSortField ? null : sortByFromUrl,
          sortDirection
        );

        // Dashboard cards (e.g. Overdue Fee) set URL params; apply the same client
        // filter used by the legacy list so Paid / non-overdue rows never remain.
        if (
          apiEndpoint?.includes("/api/ems/fee-installments") &&
          hasFeeInstallmentDashboardUrlFilters(searchParams)
        ) {
          const filteredRows = applyFeeInstallmentRowsForUrl(
            fullySortedData,
            searchParams
          );
          setData(filteredRows);
          const pageSize =
            Number.parseInt(searchParams.get("pageSize"), 10) ||
            result?.pagination?.pageSize ||
            result?.pagination?.limit ||
            25;
          const page = Number.parseInt(searchParams.get("page"), 10) || 1;
          const total = filteredRows.length;
          setPaginationData({
            page,
            pageSize,
            total,
            totalPages: Math.max(1, Math.ceil(total / Math.max(pageSize, 1))),
            hasNext: total > page * pageSize,
            hasPrev: page > 1,
          });
          return;
        }

        setData(fullySortedData);

        if (selfScopedFetch) {
          // Totals are synced from filtered rows after client self-scope filtering.
          const pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 25;
          const page = Number.parseInt(searchParams.get("page"), 10) || 1;
          setPaginationData({
            page,
            pageSize,
            total: fullySortedData.length,
            totalPages: Math.max(
              1,
              Math.ceil(fullySortedData.length / Math.max(pageSize, 1))
            ),
            hasNext: false,
            hasPrev: page > 1,
          });
        } else if (result?.pagination) {
          const total = result.pagination.total || fullySortedData.length || 0;
          const pageSize = result.pagination.pageSize || result.pagination.limit || 25;
          const totalPages = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)));
          setPaginationData({
            page: result.pagination.page || 1,
            pageSize,
            total,
            totalPages: result.pagination.totalPages || totalPages,
            hasNext: result.pagination.hasNext || false,
            hasPrev: result.pagination.hasPrev || false,
          });
        } else {
          setPaginationData(DEFAULT_METADATA_PAGINATION);
        }

        return;
      }

      const feeInstallmentParams = apiEndpoint?.includes("/api/ems/fee-installments")
        ? (() => {
            const period = String(
              searchParams.get("period") || searchParams.get("due") || ""
            ).trim();
            const month = String(searchParams.get("month") || "").trim();
            const shouldSendPeriod = !(month && period.toLowerCase() === "month");
            const status = String(searchParams.get("status") || "").trim().toLowerCase();
            return {
              ...(shouldSendPeriod && period ? { period } : null),
            ...(String(searchParams.get("due_period") || "").trim()
              ? { due_period: String(searchParams.get("due_period")).trim() }
              : null),
              ...(month ? { month } : null),
            ...(status ? { status } : null),
            ...(String(feeInstallmentSearch || "").trim()
              ? { search: String(feeInstallmentSearch).trim() }
              : null),
            };
          })()
        : {};
      const studentsParams = apiEndpoint?.includes("/api/ems/students")
        ? {
            ...(String(searchParams.get("admission_period") || "").trim()
              ? { admission_period: String(searchParams.get("admission_period")).trim() }
              : null),
            ...(String(searchParams.get("student_status") || "").trim()
              ? { student_status: String(searchParams.get("student_status")).trim() }
              : null),
            ...(String(searchParams.get("batch_status") || "").trim()
              ? { batch_status: String(searchParams.get("batch_status")).trim() }
              : null),
            ...(String(searchParams.get("batch_run_status") || "").trim()
              ? { batch_run_status: String(searchParams.get("batch_run_status")).trim() }
              : null),
            ...(String(searchParams.get("training_program") || "").trim()
              ? { training_program: String(searchParams.get("training_program")).trim() }
              : null),
            ...(String(searchParams.get("training_program_id") || "").trim()
              ? { training_program_id: String(searchParams.get("training_program_id")).trim() }
              : null),
          }
        : {};
      const admissionsTrainingProgramFilter = String(
        searchParams.get("training_program_id") ||
          searchParams.get("training_program") ||
          ""
      ).trim();
      const admissionsTrainingProgramCategoryFilter = String(
        searchParams.get("training_program_category") || ""
      ).trim();
      const admissionsParams = apiEndpoint?.includes("/api/ems/admissions")
        ? {
            ...(String(searchParams.get("admission_period") || "").trim()
              ? { admission_period: String(searchParams.get("admission_period")).trim() }
              : null),
            ...(admissionsTrainingProgramFilter
              ? {
                  // Dashboard deep links can pass either program name or program ID.
                  training_program: admissionsTrainingProgramFilter,
                  ...(String(Number(admissionsTrainingProgramFilter)) ===
                  admissionsTrainingProgramFilter
                    ? { training_program_id: admissionsTrainingProgramFilter }
                    : null),
                }
              : null),
            ...(admissionsTrainingProgramCategoryFilter
              ? {
                  training_program_category: admissionsTrainingProgramCategoryFilter,
                }
              : null),
          }
        : {};
      const offersTrainingProgramFilter = String(
        searchParams.get("training_program_id") ||
          searchParams.get("training_program") ||
          ""
      ).trim();
      const offersParams =
        apiEndpoint?.includes("/api/ems/offers") &&
        !apiEndpoint?.includes("/api/ems/offer-") &&
        offersTrainingProgramFilter &&
        offersTrainingProgramFilter.toLowerCase() !== "all"
          ? {
              training_program: offersTrainingProgramFilter,
              training_program_id: offersTrainingProgramFilter,
              organization_ems_training_program_id: offersTrainingProgramFilter,
            }
          : {};
      const batchTrainersBatchId = String(
        searchParams.get("organization_ems_batch_id") || ""
      ).trim();
      const batchTrainersTrainerId = String(
        searchParams.get("trainer_employee_id") || ""
      ).trim();
      const batchTrainersParams = (
        apiEndpoint?.includes("/api/ems/batch-trainers") ||
        apiEndpoint?.includes("/api/ems/batch-class-trainers")
      )
        ? {
            ...(batchTrainersBatchId &&
            batchTrainersBatchId.toLowerCase() !== "all"
              ? { organization_ems_batch_id: batchTrainersBatchId }
              : null),
            ...(batchTrainersTrainerId &&
            batchTrainersTrainerId.toLowerCase() !== "all"
              ? { trainer_employee_id: batchTrainersTrainerId }
              : null),
          }
        : {};
      const batchClassRecordTrainersParams = apiEndpoint?.includes(
        "/api/ems/batch-class-record-trainers"
      )
        ? {
            ...(batchTrainersTrainerId &&
            batchTrainersTrainerId.toLowerCase() !== "all"
              ? { trainer_employee_id: batchTrainersTrainerId }
              : null),
          }
        : {};
      const testsTrainingProgram = String(
        searchParams.get("training_program_id") ||
          searchParams.get("training_program") ||
          ""
      ).trim();
      const testsParams =
        apiEndpoint?.includes("/api/ems/tests") &&
        testsTrainingProgram &&
        testsTrainingProgram.toLowerCase() !== "all"
          ? { training_program: testsTrainingProgram }
          : {};
      const schedulesBatchId = String(
        searchParams.get("organization_ems_batch_id") || ""
      ).trim();
      const schedulesStatus = String(searchParams.get("status") || "").trim();
      const schedulesParams = apiEndpoint?.includes("/api/ems/assessment-schedules")
        ? {
            ...(schedulesBatchId &&
            schedulesBatchId.toLowerCase() !== "all"
              ? { organization_ems_batch_id: schedulesBatchId }
              : null),
            ...(schedulesStatus &&
            schedulesStatus.toLowerCase() !== "all"
              ? { status: schedulesStatus }
              : null),
          }
        : {};
      const offerAudienceOfferId = String(
        searchParams.get("organization_ems_offer_id") || ""
      ).trim();
      const offerAudienceScope = String(
        searchParams.get("recipient_type") || ""
      ).trim();
      const assessmentDeliveryMode = String(
        searchParams.get("assessment_delivery_mode") || ""
      ).trim();
      const assessmentTypesParams =
        apiEndpoint?.includes("/api/ems/assessment-types") &&
        assessmentDeliveryMode &&
        assessmentDeliveryMode.toLowerCase() !== "all"
          ? { assessment_delivery_mode: assessmentDeliveryMode }
          : {};
      const placementPoolCourse = String(
        searchParams.get("training_program_id") ||
          searchParams.get("training_program") ||
          ""
      ).trim();
      const placementPoolStatus = String(
        searchParams.get("batch_student_status") || ""
      ).trim();
      const placementPoolParams = apiEndpoint?.includes("/api/ems/placement-pool")
        ? {
            ...(placementPoolCourse &&
            placementPoolCourse.toLowerCase() !== "all"
              ? {
                  training_program: placementPoolCourse,
                  training_program_id: placementPoolCourse,
                  organization_ems_training_program_id: placementPoolCourse,
                }
              : null),
            ...(placementPoolStatus &&
            placementPoolStatus.toLowerCase() !== "all"
              ? { batch_student_status: placementPoolStatus }
              : null),
          }
        : {};
      const offerAudiencesParams = apiEndpoint?.includes("/api/ems/offer-audiences")
        ? {
            ...(offerAudienceOfferId &&
            offerAudienceOfferId.toLowerCase() !== "all"
              ? { organization_ems_offer_id: offerAudienceOfferId }
              : null),
            ...(offerAudienceScope &&
            offerAudienceScope.toLowerCase() !== "all"
              ? { recipient_type: offerAudienceScope }
              : null),
          }
        : {};
      const response = await axios.get(`${MAIN_URL}${apiEndpoint}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          per_page: "all",
          organization_id: organizationId,
          ...extraParams,
          ...feeInstallmentParams,
          ...studentsParams,
          ...admissionsParams,
          ...offersParams,
          ...batchTrainersParams,
          ...batchClassRecordTrainersParams,
          ...testsParams,
          ...schedulesParams,
          ...offerAudiencesParams,
          ...assessmentTypesParams,
          ...placementPoolParams,
        },
      });

      if (requestSeq !== fetchRequestSeqRef.current) return;

      const rawPayload = response.data?.data ?? response.data;
      const responseData = normalizeListPayload(rawPayload);
      const sortedResponseData =
        apiEndpoint?.includes("/api/ems/leads") && Array.isArray(responseData)
          ? [...responseData].sort((a, b) => {
              const ad = new Date(a?.lead_datetime || 0).getTime();
              const bd = new Date(b?.lead_datetime || 0).getTime();
              return bd - ad;
            })
          : apiEndpoint?.includes("/api/ems/lead-activities") &&
              Array.isArray(responseData) &&
              !useMetadataFilters
            ? [...responseData].sort((a, b) => {
                const ad = new Date(a?.activity_datetime || 0).getTime();
                const bd = new Date(b?.activity_datetime || 0).getTime();
                if (bd !== ad) return bd - ad;
                const ai =
                  Number(a?.organization_ems_lead_activity_id ?? a?.id ?? 0) || 0;
                const bi =
                  Number(b?.organization_ems_lead_activity_id ?? b?.id ?? 0) || 0;
                return bi - ai;
              })
            : responseData;
      const mappedData = await attachDashboardTrainerPhotos(
        mapResponseToRows
          ? mapResponseToRows(sortedResponseData)
          : sortedResponseData
      );

      if (feeInstallmentDashboardFilters) {
        const filteredRows = applyFeeInstallmentRowsForUrl(
          Array.isArray(mappedData) ? mappedData : [],
          searchParams
        );
        setData(filteredRows);
        const pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 25;
        const total = filteredRows.length;
        setPaginationData({
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize,
          total,
          totalPages: Math.max(1, Math.ceil(total / Math.max(pageSize, 1))),
          hasNext: total > pageSize,
          hasPrev: false,
        });
      } else {
        setData(mappedData);
      }

    } catch (err) {
      if (requestSeq !== fetchRequestSeqRef.current) return;
      console.error(`Error fetching ${title}:`, err);
      if (useMetadataFilters && filteredApiEndpoint) {
        setData([]);
        setPaginationData(DEFAULT_METADATA_PAGINATION);
      }
    } finally {
      if (requestSeq === fetchRequestSeqRef.current) {
        setLoading(false);
      }
    }
  };

  // Run on mount and pagination change
  useEffect(() => {
    if (!organizationId) return;

    setData([]);
    fetchData(true);
  }, [
    organizationId,
    apiEndpoint,
    filteredApiEndpoint,
    useMetadataFilters,
    feeInstallmentDashboardFilters,
    metadataListFetchKey,
    feeInstallmentFetchKey,
    studentsFetchKey,
    admissionsFetchKey,
    offersFetchKey,
    offerAudiencesFetchKey,
    batchTrainersFetchKey,
    testsFetchKey,
    schedulesFetchKey,
    assessmentTypesFetchKey,
    placementPoolFetchKey,
  ]);

  useEffect(() => {
    const canFetchListWidgets = (() => {
      if (!organizationId || !apiEndpoint || !summaryCardsEnabled) return false;
      if (apiEndpoint.includes("/api/ems/fee-installments")) {
        return checkPermission(Permission, "VIEW_FEE_INSTALLMENTS");
      }
      if (
        apiEndpoint.includes("/api/ems/students") &&
        !apiEndpoint.includes("/api/ems/students-fees")
      ) {
        return checkPermission(Permission, "VIEW_STUDENTS");
      }
      if (
        apiEndpoint.includes("/api/ems/batches") &&
        !apiEndpoint.includes("/api/ems/batch-")
      ) {
        return checkPermission(Permission, "VIEW_BATCHES||VIEW_SELF_BATCHES");
      }
      if (
        apiEndpoint.includes("/api/ems/leads") &&
        !apiEndpoint.includes("/api/ems/lead-")
      ) {
        return checkPermission(Permission, "VIEW_LEADS");
      }
      if (apiEndpoint.includes("/api/ems/training-programs")) {
        return checkPermission(Permission, "VIEW_TRAINING_PROGRAMS");
      }
      return false;
    })();

    if (!canFetchListWidgets) {
      setLeadsSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/leads-summary`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: { organization_id: organizationId },
        });
        if (cancelled) return;
        setLeadsSummary(response.data?.data ?? null);
      } catch (err) {
        console.error("Error fetching dashboard widgets summary:", err);
        if (!cancelled) setLeadsSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, organizationId, Permission, summaryCardsEnabled]);

  useEffect(() => {
    const needsTrainingProgramOptions =
      apiEndpoint?.includes("/api/ems/batch-students") ||
      apiEndpoint?.includes("/api/ems/students") ||
      apiEndpoint?.includes("/api/ems/admissions") ||
      apiEndpoint?.includes("/api/ems/leads") ||
      apiEndpoint?.includes("/api/ems/batches") ||
      apiEndpoint?.includes("/api/ems/batch-classes") ||
      apiEndpoint?.includes("/api/ems/assesments") ||
      apiEndpoint?.includes("/api/ems/tests") ||
      apiEndpoint?.includes("/api/ems/training-program-modules") ||
      apiEndpoint?.includes("/api/ems/module-questions") ||
      apiEndpoint?.includes("/api/ems/placement-pool") ||
      apiEndpoint?.includes("/api/ems/placement-referrals") ||
      apiEndpoint?.includes("/api/ems/certificates") ||
      apiEndpoint?.includes("/api/ems/class-records") ||
      (apiEndpoint?.includes("/api/ems/demo-sessions") &&
        !apiEndpoint?.includes("/api/ems/demo-participants")) ||
      apiEndpoint?.includes("/api/ems/demo-participants") ||
      (apiEndpoint?.includes("/api/ems/offers") &&
        !apiEndpoint?.includes("/api/ems/offer-"));
    if (!needsTrainingProgramOptions || !org?.organization_id) {
      setTrainingProgramsAllOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/training-programs`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org.organization_id,
            ...extraParams,
          },
        });
        const rawPayload = response.data?.data ?? response.data;
        const arr = normalizeListPayload(rawPayload);
        if (cancelled) return;
        const seen = new Set();
        const options = arr
          .map((item) => ({
            id:
              item?.organization_ems_training_program_id ??
              item?.training_program_id ??
              item?.id,
            name: String(item?.training_program_name || "").trim(),
          }))
          .filter((s) => s.id != null && s.name && s.name !== "-")
          .filter((s) => {
            const key = `${String(s.id).trim()}::${s.name.toLowerCase()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        setTrainingProgramsAllOptions(options);
      } catch (err) {
        console.error("Error fetching training programs for filter:", err);
        if (!cancelled) setTrainingProgramsAllOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  useEffect(() => {
    const needsLeadStageOptions =
      !!apiEndpoint &&
      apiEndpoint.includes("/api/ems/leads") &&
      !apiEndpoint.includes("/api/ems/lead-");
    if (!needsLeadStageOptions || !org?.organization_id) {
      setLeadStageOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/lead-stages`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org.organization_id,
          },
        });
        const arr = normalizeListPayload(response.data?.data ?? response.data);
        if (cancelled) return;
        const options = arr
          .map((item) => ({
            id:
              item?.organization_ems_lead_stage_id ??
              item?.lead_stage_id ??
              item?.id,
            name: String(item?.lead_stage_name || "").trim(),
            sequence: Number(item?.lead_stage_sequence_number ?? 0),
          }))
          .filter((stage) => stage.id != null && stage.name && stage.name !== "-")
          .sort((a, b) => a.sequence - b.sequence || a.name.localeCompare(b.name));
        setLeadStageOptions(options);
      } catch (err) {
        console.error("Error fetching lead stages for filter:", err);
        if (!cancelled) setLeadStageOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  useEffect(() => {
    const needsStudentOptions =
      apiEndpoint?.includes("/api/ems/students-fees") ||
      apiEndpoint?.includes("/api/ems/assesment-results") ||
      apiEndpoint?.includes("/api/ems/student-reviews");
    if (!needsStudentOptions || !org?.organization_id) {
      setFeeReceiptStudentOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/students`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org.organization_id,
          },
        });
        const arr = normalizeListPayload(response.data?.data ?? response.data);
        if (cancelled) return;
        const options = arr
          .map((item) => {
            const id =
              item?.organization_ems_student_id ?? item?.student_id ?? item?.id;
            const name = [item?.first_name, item?.middle_name, item?.last_name]
              .map((part) => String(part || "").trim())
              .filter(Boolean)
              .join(" ");
            const code = String(
              item?.student_code || item?.person_code || ""
            ).trim();
            const label = name
              ? code
                ? `${name} (${code})`
                : name
              : code;
            return { id, name: label };
          })
          .filter((student) => student.id != null && student.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setFeeReceiptStudentOptions(options);
      } catch (err) {
        console.error("Error fetching students for fee receipt filter:", err);
        if (!cancelled) setFeeReceiptStudentOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  useEffect(() => {
    const needsBatchTrainerFilters =
      apiEndpoint?.includes("/api/ems/batch-trainers") ||
      apiEndpoint?.includes("/api/ems/batch-class-trainers") ||
      apiEndpoint?.includes("/api/ems/batch-class-record-trainers") ||
      apiEndpoint?.includes("/api/ems/assessment-schedules") ||
      apiEndpoint?.includes("/api/ems/certificates");
    if (!needsBatchTrainerFilters || !org?.organization_id) {
      setBatchTrainerBatchOptions([]);
      setBatchTrainerTrainerOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };
        const [batchResponse, trainerResponse] = await Promise.all([
          axios.get(`${MAIN_URL}/api/ems/batches`, {
            headers,
            params: {
              per_page: "all",
              organization_id: org.organization_id,
            },
          }),
          axios.get(
            `${MAIN_URL}/api/organizations/${org.organization_id}/users-data-all`,
            { headers }
          ),
        ]);
        if (cancelled) return;
        const batches = normalizeListPayload(
          batchResponse.data?.data ?? batchResponse.data
        )
          .map((item) => {
            const id = item?.organization_ems_batch_id ?? item?.id;
            const name = String(item?.batch_name || "").trim();
            const code = String(item?.batch_code || "").trim();
            const label = name ? (code ? `${name} (${code})` : name) : code;
            return { id, name: label };
          })
          .filter((batch) => batch.id != null && batch.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        const trainers = normalizeListPayload(
          trainerResponse.data?.users ?? trainerResponse.data?.data ?? trainerResponse.data
        )
          .map((item) => {
            const id = item?.organization_user_id ?? item?.id;
            const name = String(
              item?.full_name ||
                [item?.first_name, item?.last_name].filter(Boolean).join(" ")
            ).trim();
            return { id, name };
          })
          .filter((trainer) => trainer.id != null && trainer.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setBatchTrainerBatchOptions(batches);
        setBatchTrainerTrainerOptions(trainers);
      } catch (err) {
        console.error("Error fetching batch trainer filters:", err);
        if (!cancelled) {
          setBatchTrainerBatchOptions([]);
          setBatchTrainerTrainerOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  useEffect(() => {
    const needsTimezones = apiEndpoint?.includes("/api/ems/lead-contact-timings");
    if (!needsTimezones || !org?.organization_id) {
      setContactTimingTimezoneOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}${apiEndpoint}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org.organization_id,
          },
        });
        if (cancelled) return;
        const rows = normalizeListPayload(response.data?.data ?? response.data);
        const zones = Array.from(
          new Set(
            rows
              .map((item) => String(item?.preferred_contact_timezone || "").trim())
              .filter((zone) => zone && zone !== "-")
          )
        ).sort((a, b) => a.localeCompare(b));
        setContactTimingTimezoneOptions(zones);
      } catch (err) {
        console.error("Error fetching contact timing timezones:", err);
        if (!cancelled) setContactTimingTimezoneOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  useEffect(() => {
    if (!apiEndpoint?.includes("/api/ems/offer-audiences") || !org?.organization_id) {
      setOfferAudienceOfferOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/offers`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org.organization_id,
          },
        });
        if (cancelled) return;
        const rows = normalizeListPayload(response.data?.data ?? response.data);
        const options = rows
          .map((item) => ({
            id: item?.organization_ems_offer_id ?? item?.id,
            name: String(item?.offer_name || item?.offer_code || "").trim(),
          }))
          .filter((item) => item.id != null && item.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setOfferAudienceOfferOptions(options);
      } catch (err) {
        console.error("Error fetching offers for audience filter:", err);
        if (!cancelled) setOfferAudienceOfferOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  useEffect(() => {
    const needsAgencyFilters = apiEndpoint?.includes("/api/ems/recruitment-agencies");
    if (!needsAgencyFilters || !org?.organization_id) {
      setRecruitmentAgencyNameOptions([]);
      setRecruitmentAgencyCountryOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/recruitment-agencies`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org.organization_id,
          },
        });
        if (cancelled) return;
        const rows = normalizeListPayload(response.data?.data ?? response.data);
        const names = Array.from(
          new Set(
            rows
              .map((item) => String(item?.agency_name || "").trim())
              .filter((name) => name && name !== "-")
          )
        ).sort((a, b) => a.localeCompare(b));
        const countries = Array.from(
          new Set(
            rows
              .map((item) => String(item?.country?.country_name || item?.country_name || "").trim())
              .filter((name) => name && name !== "-")
          )
        ).sort((a, b) => a.localeCompare(b));
        setRecruitmentAgencyNameOptions(names);
        setRecruitmentAgencyCountryOptions(countries);
      } catch (err) {
        console.error("Error fetching recruitment agency filters:", err);
        if (!cancelled) {
          setRecruitmentAgencyNameOptions([]);
          setRecruitmentAgencyCountryOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  useEffect(() => {
    const needsReferralAgencies = apiEndpoint?.includes("/api/ems/placement-referrals");
    if (!needsReferralAgencies || !org?.organization_id) {
      setPlacementReferralAgencyOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/recruitment-agencies`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org.organization_id,
          },
        });
        if (cancelled) return;
        const rows = normalizeListPayload(response.data?.data ?? response.data);
        const seen = new Set();
        setPlacementReferralAgencyOptions(
          rows
            .map((item) => ({
              id: item?.organization_ems_recruitment_agency_id,
              name: String(item?.agency_name || "").trim(),
            }))
            .filter((item) => item.id != null && item.name && item.name !== "-")
            .filter((item) => {
              const key = String(item.id);
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch (err) {
        console.error("Error fetching recruitment agencies for referral filter:", err);
        if (!cancelled) setPlacementReferralAgencyOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  useEffect(() => {
    const needsAssistanceAdmissions = apiEndpoint?.includes("/api/ems/placement-assistance");
    if (!needsAssistanceAdmissions || !org?.organization_id) {
      setPlacementAssistanceAdmissionOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/admissions`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org.organization_id,
          },
        });
        if (cancelled) return;
        const rows = normalizeListPayload(response.data?.data ?? response.data);
        const seen = new Set();
        setPlacementAssistanceAdmissionOptions(
          rows
            .map((item) => {
              const studentName = [
                item?.student?.first_name,
                item?.student?.last_name,
              ]
                .filter(Boolean)
                .join(" ")
                .trim();
              const number = String(item?.admission_number || "").trim();
              return {
                id: item?.organization_ems_admission_id,
                name: studentName && number ? `${number} — ${studentName}` : number || studentName,
              };
            })
            .filter((item) => item.id != null && item.name && item.name !== "-")
            .filter((item) => {
              const key = String(item.id);
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch (err) {
        console.error("Error fetching admissions for placement assistance filter:", err);
        if (!cancelled) setPlacementAssistanceAdmissionOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  useEffect(() => {
    const needsCompanyFilters = apiEndpoint?.includes("/api/ems/companies");
    if (!needsCompanyFilters || !org?.organization_id) {
      setCompanyIndustryOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/companies`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org.organization_id,
          },
        });
        if (cancelled) return;
        const industries = Array.from(
          new Set(
            normalizeListPayload(response.data?.data ?? response.data)
              .map((item) => String(item?.industry || "").trim())
              .filter((name) => name && name !== "-")
          )
        ).sort((a, b) => a.localeCompare(b));
        setCompanyIndustryOptions(industries);
      } catch (err) {
        console.error("Error fetching company filters:", err);
        if (!cancelled) setCompanyIndustryOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  useEffect(() => {
    const needsProgramFilters =
      apiEndpoint?.includes("/api/ems/training-programs") &&
      !apiEndpoint?.includes("/api/ems/training-programs-categories") &&
      !apiEndpoint?.includes("/api/ems/training-program-modules");
    if (!needsProgramFilters || !org?.organization_id) {
      setTrainingProgramCategoryOptions([]);
      setTrainingProgramMonthOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };
        const params = {
          per_page: "all",
          organization_id: org.organization_id,
        };
        const [categoryResponse, programResponse] = await Promise.all([
          axios.get(`${MAIN_URL}/api/ems/training-programs-categories`, {
            headers,
            params,
          }),
          axios.get(`${MAIN_URL}/api/ems/training-programs`, { headers, params }),
        ]);
        if (cancelled) return;
        const categories = normalizeListPayload(
          categoryResponse.data?.data ?? categoryResponse.data
        )
          .map((item) => ({
            id: item?.organization_ems_training_program_category_id ?? item?.id,
            name: String(item?.training_program_category_name || "").trim(),
          }))
          .filter((item) => item.id != null && item.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        const months = Array.from(
          new Set(
            normalizeListPayload(programResponse.data?.data ?? programResponse.data)
              .map((item) => item?.duration_in_months)
              .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
              .map((value) => String(value).trim())
          )
        ).sort((a, b) => Number(a) - Number(b));
        setTrainingProgramCategoryOptions(categories);
        setTrainingProgramMonthOptions(months);
      } catch (err) {
        console.error("Error fetching training program filters:", err);
        if (!cancelled) {
          setTrainingProgramCategoryOptions([]);
          setTrainingProgramMonthOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  useEffect(() => {
    const needsAssessmentTypeOptions = apiEndpoint?.includes("/api/ems/assesments");
    if (!needsAssessmentTypeOptions || !org?.organization_id) {
      setAssessmentTypeOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/assessment-types`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org.organization_id,
          },
        });
        if (cancelled) return;
        const options = normalizeListPayload(response.data?.data ?? response.data)
          .map((item) => {
            const id = item?.organization_ems_assessment_type_id ?? item?.id;
            const name = String(item?.assessment_type_name || "").trim();
            const mode = String(item?.assessment_delivery_mode || "").trim();
            return { id, name: mode ? `${name} (${mode})` : name };
          })
          .filter((type) => type.id != null && type.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setAssessmentTypeOptions(options);
      } catch (err) {
        console.error("Error fetching assessment type filters:", err);
        if (!cancelled) setAssessmentTypeOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  const periodFilter =
    searchParams.get("period") || searchParams.get("due");
  const batchIdFilter = searchParams.get("organization_ems_batch_id");
  const trainingProgramFilter =
    searchParams.get("training_program_id") ||
    searchParams.get("training_program");
  const trainingProgramCategoryFilter = searchParams.get("training_program_category");
  const leadStageFilter = searchParams.get("lead_stage");
  const leadDateFilter = searchParams.get("lead_date");
  const monthFilter = searchParams.get("month");
  const enrolledFilter = searchParams.get("enrolled");
  const statusFilter = searchParams.get("status");
  const duePeriodFilter = searchParams.get("due_period");
  const attendanceStatusFilter = searchParams.get("attendance_status");
  const batchStatusFilter = searchParams.get("batch_status");
  const batchRunStatusFilter = searchParams.get("batch_run_status");
  const batchModeFilter = searchParams.get("mode");
  const leadStatusFilter = searchParams.get("lead_status");
  const leadActivityLeadIdFilter = searchParams.get("organization_ems_lead_id");
  const resolvedTableConfig = useMemo(() => {
    let baseConfig = config;

    if (apiEndpoint?.includes("/api/ems/leads")) {
      baseConfig = {
        ...config,
        pagination: {
          ...(config?.pagination || {}),
          rowsPerPage: config?.pagination?.rowsPerPage || 25,
        },
        sort: {
          field:
            config?.sort?.field && config?.sort?.direction
              ? config.sort.field
              : "lead_datetime",
          direction:
            config?.sort?.field && config?.sort?.direction
              ? config.sort.direction
              : "desc",
        },
      };
    }

    return tableConfig ? { ...baseConfig, ...tableConfig } : baseConfig;
  }, [apiEndpoint, config, tableConfig]);

  const rowsForTable = useMemo(() => {
    if (apiEndpoint?.includes("/api/ems/demo-sessions")) {
      let filtered = data;
      if (!useMetadataFilters) {
        if (periodFilter === "today") {
          filtered = filterDemoSessionsRowsToday(filtered);
        }
        if (periodFilter === "month") {
          filtered = filterDemoSessionsRowsByMonth(filtered, monthFilter);
        }
      }
      // Self-demo permission: only show trainer-matched sessions for logged-in user.
      if (canViewSelfDemoSessions && !canViewAllDemoSessions) {
        const selfRows = filtered.filter((row) =>
          emsRowMatchesLoggedInTrainer(row, userData)
        );
        return selfRows;
      }
      return filtered;
    }
    if (apiEndpoint?.includes("/api/ems/demo-participants")) {
      let filtered = data;
      if (canViewSelfDemoParticipants && !canViewAllDemoParticipants) {
        filtered = filtered.filter((row) =>
          emsRowMatchesLoggedInTrainer(row, userData)
        );
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/admissions")) {
      let filtered = data;
      if (!useMetadataFilters) {
        if (periodFilter === "today") {
          filtered = filterAdmissionsRowsAdmissionToday(filtered);
        }
        if (periodFilter === "month") {
          filtered = filterAdmissionsRowsByMonth(filtered, monthFilter);
        }

        // enrolled=1 -> only show admissions that represent enrollments
        if (String(enrolledFilter || "") === "1") {
          const allowed = new Set(["confirmed", "completed"]);
          filtered = filtered.filter((row) =>
            allowed.has(String(row?.admission_status || "").trim().toLowerCase())
          );
        }
      }

      if (trainingProgramFilter) {
        const selectedProgramId = String(trainingProgramFilter).trim();
        const selectedProgramIdIsNumeric =
          String(Number(selectedProgramId)) === selectedProgramId;
        const selectedProgram = (trainingProgramsAllOptions || []).find(
          (p) => String(p?.id ?? "").trim() === selectedProgramId
        );
        const selectedProgramName = String(
          selectedProgram?.name ??
            // Dashboard graph deep links may pass program name instead of ID.
            (selectedProgramIdIsNumeric ? "" : selectedProgramId)
        )
          .trim()
          .toLowerCase();
        filtered = filtered.filter((row) => {
          const rowProgramId = String(
            row?.training_program_id ??
              row?.organization_ems_training_program_id ??
              row?.training_program?.organization_ems_training_program_id ??
              ""
          ).trim();
          const rowProgramName = String(
            row?.training_program ??
              row?.training_program?.training_program_name ??
              ""
          )
            .trim()
            .toLowerCase();
          return (
            (rowProgramId && rowProgramId === selectedProgramId) ||
            (selectedProgramName &&
              (rowProgramName === selectedProgramName ||
                rowProgramName.includes(selectedProgramName)))
          );
        });
      }

      if (trainingProgramCategoryFilter) {
        const q = String(trainingProgramCategoryFilter).trim().toLowerCase();
        const hasCategoryData = filtered.some((row) => {
          const v = String(row?.training_program_category || "").trim();
          return v && v !== "-";
        });

        // Apply category filter only when category values are actually present.
        // If category is unavailable in admissions payload, don't hide valid program matches.
        if (hasCategoryData) {
          filtered = filtered.filter((row) =>
            String(row?.training_program_category || "")
              .trim()
              .toLowerCase()
              .includes(q)
          );
        }
      }

      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/fee-installments")) {
      // When Overdue / Due / Collected cards set URL params, always honor them
      // (metadata path previously skipped client filtering and left Paid rows visible).
      if (feeInstallmentDashboardFilters) {
        return applyFeeInstallmentRowsForUrl(data, searchParams);
      }

      // Metadata filtered API already applies server-side sort/filter — do not re-sort client-side.
      if (useMetadataFilters) {
        return data;
      }

      // Legacy GET list path (non-metadata): client filters + default due-date ordering.
      let filtered = data;
      const period = String(periodFilter || "").trim().toLowerCase();
      const month = String(monthFilter || "").trim();
      const status = String(statusFilter || "").trim().toLowerCase();
      const duePeriod = String(duePeriodFilter || "").trim().toLowerCase();
      const usesServerFilters =
        Boolean(status) || Boolean(period) || Boolean(month) || Boolean(duePeriod);

      if (!usesServerFilters) {
        if (month) {
          filtered = filterFeeInstallmentsByDueMonth(filtered, month);
        } else if (period === "today" || period === "month") {
          filtered = filterFeeInstallmentsByDuePeriod(filtered, period);
        } else if (period === "overdue") {
          filtered = filterFeeInstallmentsOverdue(filtered);
        }
      } else if (month) {
        filtered = filterFeeInstallmentsByDueMonth(filtered, month);
      }

      if (status && !usesServerFilters) {
        if (status === "overdue") {
          filtered = filterFeeInstallmentsOverdue(filtered);
        } else {
          filtered = filtered.filter(
            (row) => String(row?.status ?? "").trim().toLowerCase() === status
          );
        }
      }

      // Sorting rule:
      // 1) Pending installments first
      // 2) Within Pending, due-date nearest to today first
      // 3) Remaining rows by due date ascending
      const today = new Date();
      const startOfToday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      ).getTime();
      const parseDueTs = (row) => {
        const raw =
          row?.payment_date_prefill ??
          row?.due_date ??
          row?.due_date_raw ??
          null;
        const ts = raw ? new Date(raw).getTime() : Number.NaN;
        return Number.isFinite(ts) ? ts : Number.POSITIVE_INFINITY;
      };
      return [...filtered].sort((a, b) => {
        const aPending = String(a?.status ?? "").trim().toLowerCase() === "pending";
        const bPending = String(b?.status ?? "").trim().toLowerCase() === "pending";
        if (aPending !== bPending) return aPending ? -1 : 1;

        const ad = parseDueTs(a);
        const bd = parseDueTs(b);

        if (aPending && bPending) {
          const aDiff = Math.abs(ad - startOfToday);
          const bDiff = Math.abs(bd - startOfToday);
          if (aDiff !== bDiff) return aDiff - bDiff;
          return ad - bd;
        }

        return ad - bd;
      });
    }

    if (
      apiEndpoint?.includes("/api/ems/lead-activities") &&
      !useMetadataFilters
    ) {
      let filtered = data;
      if (
        leadActivityLeadIdFilter !== null &&
        String(leadActivityLeadIdFilter).trim() !== ""
      ) {
        const q = String(leadActivityLeadIdFilter).trim();
        filtered = filtered.filter((row) =>
          String(row?.organization_ems_lead_id ?? "").trim() === q
        );
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/batches")) {
      let filtered = data;
      if (seesOnlyOwnBatches(Permission, userData)) {
        filtered = filtered.filter((row) =>
          emsBatchListRowMatchesLoggedInTrainer(row, userData)
        );
      }
      if (!useMetadataFilters) {
        if (trainingProgramFilter != null && String(trainingProgramFilter).trim() !== "") {
          const selectedProgram = (trainingProgramsAllOptions || []).find(
            (p) => String(p?.id ?? "").trim() === String(trainingProgramFilter).trim()
          );
          const selectedProgramName = String(selectedProgram?.name ?? "")
            .trim()
            .toLowerCase();
          if (selectedProgramName) {
            filtered = filtered.filter(
              (row) =>
                String(row?.training_program ?? "").trim().toLowerCase() === selectedProgramName
            );
          }
        }
        const batchPeriod = String(periodFilter || "").trim().toLowerCase();
        if (batchPeriod === "starting_soon" || batchPeriod === "ending_soon") {
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const horizon = new Date(today);
          horizon.setDate(horizon.getDate() + 14);
          const parseYmd = (value) => {
            if (!value || value === "-") return null;
            const s = String(value).trim();
            const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            const d = new Date(s);
            return Number.isNaN(d.getTime()) ? null : d;
          };
          filtered = filtered.filter((row) => {
            if (batchPeriod === "starting_soon") {
              if (String(row?.status ?? "").trim().toLowerCase() !== "scheduled") return false;
              const start = parseYmd(row?.start_date);
              if (!start) return false;
              return start >= today && start <= horizon;
            }
            if (String(row?.status ?? "").trim().toLowerCase() !== "ongoing") return false;
            const end = parseYmd(row?.end_date);
            if (!end) return false;
            return end >= today && end <= horizon;
          });
        } else if (statusFilter != null && String(statusFilter).trim() !== "") {
          const q = String(statusFilter).trim().toLowerCase();
          filtered = filtered.filter(
            (row) => String(row?.status ?? "").trim().toLowerCase() === q
          );
        }
        if (batchModeFilter != null && String(batchModeFilter).trim() !== "") {
          const q = String(batchModeFilter).trim().toLowerCase();
          filtered = filtered.filter(
            (row) =>
              String(row?.batch_mode ?? row?.mode ?? "")
                .trim()
                .toLowerCase() === q
          );
        }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/batch-classes")) {
      let filtered = data;
      if (canViewSelfBatchClasses && !canViewAllBatchClasses) {
        filtered = filtered.filter((row) =>
          emsRowMatchesLoggedInTrainer(row, userData)
        );
      }
      if (!useMetadataFilters) {
      if (batchIdFilter != null && String(batchIdFilter).trim() !== "") {
        const q = String(batchIdFilter).trim();
        filtered = filtered.filter(
          (row) =>
            String(
              row?.organization_ems_batch_id ??
                row?.batch?.organization_ems_batch_id ??
                ""
            ).trim() === q
        );
      }
      const batchActivity = String(searchParams.get("batch_activity") || "active")
        .trim()
        .toLowerCase();
      if (batchActivity === "active" || batchActivity === "inactive") {
        filtered = filtered.filter((row) => {
          const status = String(row?.batch_status ?? row?.batch?.status ?? "")
            .trim()
            .toLowerCase();
          const isActive = status === "ongoing";
          return batchActivity === "active" ? isActive : !isActive;
        });
      }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/batch-students")) {
      let filtered = data;
      if (canViewSelfBatchStudents && !canViewAllBatchStudents) {
        filtered = filtered.filter((row) =>
          emsBatchListRowMatchesLoggedInTrainer(row, userData)
        );
      }
      if (!useMetadataFilters) {
      if (batchIdFilter != null && String(batchIdFilter).trim() !== "") {
        const q = String(batchIdFilter).trim();
        filtered = filtered.filter(
          (row) =>
            String(
              row?.organization_ems_batch_id ??
                row?.batch?.organization_ems_batch_id ??
                ""
            ).trim() === q
        );
      }
      if (trainingProgramFilter != null && String(trainingProgramFilter).trim() !== "") {
        const q = String(trainingProgramFilter).trim();
        filtered = filtered.filter(
          (row) => String(row?.training_program_id ?? "").trim() === q
        );
      }
      if (batchModeFilter != null && String(batchModeFilter).trim() !== "") {
        const q = String(batchModeFilter).trim().toLowerCase();
        filtered = filtered.filter(
          (row) => String(row?.mode ?? "").trim().toLowerCase() === q
        );
      }
      {
        const rawStatus = String(batchStatusFilter ?? "").trim().toLowerCase();
        const effectiveStatus = rawStatus === "" ? "pursuing" : rawStatus;
        if (effectiveStatus !== "all") {
          filtered = filtered.filter(
            (row) =>
              String(row?.batch_status ?? "").trim().toLowerCase() ===
              effectiveStatus
          );
        }
      }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/class-records")) {
      let filtered = data;
      if (canViewSelfBatchClassRecords && !canViewAllBatchClassRecords) {
        filtered = filtered.filter((row) =>
          emsClassRecordMatchesLoggedInTrainer(row, userData)
        );
      }
      if (!useMetadataFilters) {
      if (trainingProgramFilter != null && String(trainingProgramFilter).trim() !== "") {
        const q = String(trainingProgramFilter).trim().toLowerCase();
        const selectedProgram = (trainingProgramsAllOptions || []).find(
          (p) => String(p?.id ?? "").trim() === String(trainingProgramFilter).trim()
        );
        const selectedProgramName = String(selectedProgram?.name ?? "")
          .trim()
          .toLowerCase();
        filtered = filtered.filter((row) => {
          const name = String(row?.training_program ?? "").trim().toLowerCase();
          if (!name) return false;
          return name === q || (selectedProgramName && name === selectedProgramName);
        });
      }
      if (statusFilter != null && String(statusFilter).trim() !== "") {
        const q = String(statusFilter).trim().toLowerCase();
        filtered = filtered.filter(
          (row) => String(row?.status ?? "").trim().toLowerCase() === q
        );
      }
      if (batchIdFilter != null && String(batchIdFilter).trim() !== "") {
        const q = String(batchIdFilter).trim();
        filtered = filtered.filter(
          (row) =>
            String(
              row?.organization_ems_batch_id ??
                row?.batch?.organization_ems_batch_id ??
                ""
            ).trim() === q
        );
      }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/classes-attendance")) {
      let filtered = data;
      if (canViewSelfClassAttendance && !canViewAllClassAttendance) {
        filtered = filtered.filter((row) =>
          emsRowMatchesLoggedInTrainer(row, userData)
        );
      }
      if (!useMetadataFilters) {
      if (
        attendanceStatusFilter != null &&
        String(attendanceStatusFilter).trim() !== ""
      ) {
        const q = String(attendanceStatusFilter).trim().toLowerCase();
        filtered = filtered.filter(
          (row) =>
            String(row?.attendance_status ?? "").trim().toLowerCase() === q
        );
      }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/students")) {
      let filtered = data;
      if (!useMetadataFilters) {
      const studentStatusFilter = searchParams.get("student_status");
      if (studentStatusFilter != null && String(studentStatusFilter).trim() !== "") {
        const q = String(studentStatusFilter).trim().toLowerCase();
        if (q === "inactive") {
          filtered = filtered.filter((row) => {
            const st = String(row?.student_status ?? "").trim().toLowerCase();
            return st === "dropped" || st === "on hold";
          });
        } else {
          filtered = filtered.filter(
            (row) => String(row?.student_status ?? "").trim().toLowerCase() === q
          );
        }
      }
      const dashboardBatchStatus = String(
        searchParams.get("batch_status") || ""
      )
        .trim()
        .toLowerCase();
      const dashboardBatchRunStatus = String(
        searchParams.get("batch_run_status") || ""
      )
        .trim()
        .toLowerCase();
      if (
        dashboardBatchStatus === "pursuing" &&
        dashboardBatchRunStatus === "ongoing"
      ) {
        filtered = filtered.filter((row) => {
          const enrollments = Array.isArray(row?.studnet_batches)
            ? row.studnet_batches
            : [];
          return enrollments.some((enrollment) => {
            const studentBatchStatus = String(
              enrollment?.batch_status ?? ""
            )
              .trim()
              .toLowerCase();
            const runStatus = String(
              enrollment?.batch?.status ?? enrollment?.batch_run_status ?? ""
            )
              .trim()
              .toLowerCase();
            if (studentBatchStatus !== "pursuing" || runStatus !== "ongoing") {
              return false;
            }
            const programId = String(
              searchParams.get("training_program") ||
                searchParams.get("training_program_id") ||
                ""
            ).trim();
            if (!programId) return true;
            const enrollmentProgramId = String(
              enrollment?.batch?.training_program_id ??
                enrollment?.training_program_id ??
                enrollment?.batch?.training_program?.organization_ems_training_program_id ??
                ""
            ).trim();
            return !enrollmentProgramId || enrollmentProgramId === programId;
          });
        });
      }
      if (trainingProgramFilter != null && String(trainingProgramFilter).trim() !== "") {
        const selectedProgramId = String(trainingProgramFilter).trim();
        const selectedProgram = (trainingProgramsAllOptions || []).find(
          (p) => String(p?.id ?? "").trim() === selectedProgramId
        );
        const selectedProgramName = String(selectedProgram?.name ?? "")
          .trim()
          .toLowerCase();
        filtered = filtered.filter((row) => {
          const programIds = [
            row?.student_training_program_id,
            row?.training_program_id,
            ...(Array.isArray(row?.student_admissions)
              ? row.student_admissions.map(
                  (a) =>
                    a?.training_program_id ??
                    a?.training_program?.organization_ems_training_program_id ??
                    a?.training_program?.training_program_id
                )
              : []),
            ...(Array.isArray(row?.studnet_batches)
              ? row.studnet_batches.map(
                  (sb) =>
                    sb?.batch?.training_program_id ??
                    sb?.training_program_id ??
                    sb?.batch?.training_program?.organization_ems_training_program_id
                )
              : []),
          ]
            .map((v) => String(v ?? "").trim())
            .filter(Boolean);

          const programNames = [
            row?.student_training_program,
            ...(Array.isArray(row?.student_admissions)
              ? row.student_admissions.map((a) => a?.training_program?.training_program_name)
              : []),
            ...(Array.isArray(row?.studnet_batches)
              ? row.studnet_batches.map((sb) => sb?.batch?.training_program?.training_program_name)
              : []),
          ]
            .map((v) => String(v ?? "").trim().toLowerCase())
            .filter(Boolean);

          return (
            programIds.includes(selectedProgramId) ||
            (selectedProgramName && programNames.includes(selectedProgramName))
          );
        });
      }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/leads") && !useMetadataFilters) {
      let filtered = data;
      if (basePath === "/lead-activity-report") {
        filtered = filterLeadsRowsByLeadStatus(filtered, "open");
      }
      if (periodFilter === "today") {
        filtered = filterLeadsRowsByPeriod(filtered, periodFilter);
      }
      if (periodFilter === "month") {
        filtered = monthFilter
          ? filterLeadsRowsByMonth(filtered, monthFilter)
          : filterLeadsRowsByPeriod(filtered, periodFilter);
      }
      if (leadStageFilter) {
        const q = String(leadStageFilter).trim().toLowerCase();
        filtered = filtered.filter((row) => {
          const stageName = String(row?.lead_stage_name || "")
            .trim()
            .toLowerCase();
          const stageId = String(row?.lead_stage_id ?? "")
            .trim()
            .toLowerCase();
          return stageName === q || stageId === q;
        });
      }
      if (trainingProgramFilter != null && String(trainingProgramFilter).trim() !== "") {
        const q = String(trainingProgramFilter).trim().toLowerCase();
        const selectedProgram = (trainingProgramsAllOptions || []).find(
          (p) => String(p?.id ?? "").trim() === String(trainingProgramFilter).trim()
        );
        const selectedProgramName = String(selectedProgram?.name ?? "")
          .trim()
          .toLowerCase();
        filtered = filtered.filter((row) => {
          const name = String(row?.training_program ?? "").trim().toLowerCase();
          if (!name) return false;
          return name === q || (selectedProgramName && name === selectedProgramName);
        });
      }
      if (leadDateFilter) {
        const target = parseLeadDateFilter(leadDateFilter);
        if (target && !Number.isNaN(target.getTime())) {
          filtered = filtered.filter((row) => {
            const dt = parseLeadListDateTime(row.lead_datetime);
            if (!dt || Number.isNaN(dt.getTime())) return false;
            return (
              dt.getFullYear() === target.getFullYear() &&
              dt.getMonth() === target.getMonth() &&
              dt.getDate() === target.getDate()
            );
          });
        }
      }
      if (searchParams.has("lead_status")) {
        const trimmedStatus =
          leadStatusFilter != null ? String(leadStatusFilter).trim() : "";
        if (trimmedStatus.toLowerCase() !== "all") {
          filtered = filterLeadsRowsByLeadStatus(filtered, leadStatusFilter);
        }
      } else if (basePath !== "/lead-activity-report") {
        filtered = filterLeadsRowsByLeadStatus(filtered, "open");
      }
      return filtered;
    }

    if (
      apiEndpoint?.includes("/api/ems/offers") &&
      !apiEndpoint?.includes("/api/ems/offer-") &&
      trainingProgramFilter != null &&
      String(trainingProgramFilter).trim() !== "" &&
      String(trainingProgramFilter).trim().toLowerCase() !== "all"
    ) {
      const selectedProgramId = String(trainingProgramFilter).trim();
      const selectedProgram = (trainingProgramsAllOptions || []).find(
        (program) => String(program?.id ?? "").trim() === selectedProgramId
      );
      const selectedProgramName = String(selectedProgram?.name ?? "")
        .trim()
        .toLowerCase();
      return (Array.isArray(data) ? data : []).filter((row) => {
        const rowProgramId = String(
          row?.organization_ems_training_program_id ??
            row?.training_program_id ??
            ""
        ).trim();
        const rowProgramName = String(row?.program_name ?? row?.training_program ?? "")
          .trim()
          .toLowerCase();
        if (rowProgramId && rowProgramId === selectedProgramId) return true;
        return Boolean(selectedProgramName) && rowProgramName === selectedProgramName;
      });
    }

    if (apiEndpoint?.includes("/api/ems/leads") && useMetadataFilters) {
      if (!searchParams.has("lead_status")) {
        return filterLeadsRowsByLeadStatus(data, "open");
      }
      const trimmedStatus =
        leadStatusFilter != null ? String(leadStatusFilter).trim() : "";
      if (trimmedStatus.toLowerCase() === "all") {
        return data;
      }
      return filterLeadsRowsByLeadStatus(data, leadStatusFilter);
    }

    return data;
  }, [
    data,
    basePath,
    periodFilter,
    batchIdFilter,
    apiEndpoint,
    trainingProgramFilter,
    trainingProgramCategoryFilter,
    trainingProgramsAllOptions,
    leadStageFilter,
    trainingProgramsAllOptions,
    leadDateFilter,
    monthFilter,
    enrolledFilter,
    statusFilter,
    duePeriodFilter,
    attendanceStatusFilter,
    batchStatusFilter,
    batchRunStatusFilter,
    batchModeFilter,
    leadStatusFilter,
    leadActivityLeadIdFilter,
    canViewSelfDemoSessions,
    canViewAllDemoSessions,
    canViewSelfDemoParticipants,
    canViewAllDemoParticipants,
    canViewSelfBatches,
    canViewAllBatches,
    canViewSelfBatchStudents,
    canViewAllBatchStudents,
    canViewSelfBatchClasses,
    canViewAllBatchClasses,
    canViewSelfBatchClassRecords,
    canViewAllBatchClassRecords,
    canViewSelfClassAttendance,
    canViewAllClassAttendance,
    userData,
    feeInstallmentDashboardFilters,
    useMetadataFilters,
    searchParams,
  ]);

  const selfScopedListPage =
    Number.parseInt(searchParams.get("page"), 10) || 1;
  const selfScopedListPageSize =
    Number.parseInt(searchParams.get("pageSize"), 10) || 25;

  useEffect(() => {
    if (!useMetadataFilters || !isSelfScopedTrainerList) return;

    const total = Array.isArray(rowsForTable) ? rowsForTable.length : 0;
    const pageSize = Math.max(1, selfScopedListPageSize);
    const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
    let page = Math.max(1, selfScopedListPage);

    if (page > totalPages) {
      page = totalPages;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (String(next.get("page") || "1") === String(page)) return prev;
        next.set("page", String(page));
        return next;
      });
    }

    setPaginationData({
      page,
      pageSize,
      total,
      totalPages,
      hasNext: page < totalPages && total > 0,
      hasPrev: page > 1,
    });
  }, [
    useMetadataFilters,
    isSelfScopedTrainerList,
    rowsForTable,
    selfScopedListPage,
    selfScopedListPageSize,
    setSearchParams,
  ]);

  const displayRowsForTable = useMemo(() => {
    if (!(useMetadataFilters && isSelfScopedTrainerList)) {
      return rowsForTable;
    }
    const page = Math.max(1, selfScopedListPage);
    const pageSize = Math.max(1, selfScopedListPageSize);
    const start = (page - 1) * pageSize;
    return (Array.isArray(rowsForTable) ? rowsForTable : []).slice(
      start,
      start + pageSize
    );
  }, [
    rowsForTable,
    useMetadataFilters,
    isSelfScopedTrainerList,
    selfScopedListPage,
    selfScopedListPageSize,
  ]);

  const isLeadsApiList = apiEndpoint?.includes("/api/ems/leads");
  const isAdmissionsApiList = apiEndpoint?.includes("/api/ems/admissions");
  const isStudentsApiList =
    !!apiEndpoint &&
    apiEndpoint.includes("/api/ems/students") &&
    !apiEndpoint.includes("/api/ems/students-fees");
  const isFeeInstallmentsApiList = apiEndpoint?.includes(
    "/api/ems/fee-installments"
  );
  const isBatchesApiList =
    !!apiEndpoint &&
    apiEndpoint.includes("/api/ems/batches") &&
    !apiEndpoint.includes("/api/ems/batch-");
  const isTrainingProgramsApiList =
    !!apiEndpoint &&
    apiEndpoint.includes("/api/ems/training-programs") &&
    !apiEndpoint.includes("/api/ems/training-programs-categories") &&
    !apiEndpoint.includes("/api/ems/training-program-modules");
  const listWidgetGroup = isFeeInstallmentsApiList
    ? "fee"
    : isStudentsApiList
      ? "students"
      : isBatchesApiList
        ? "batches"
        : isTrainingProgramsApiList
          ? "training_programs"
          : null;

  const canShowListWidgets = useMemo(() => {
    if (!listWidgetGroup) return false;
    if (listWidgetGroup === "fee") {
      return hasPermission("VIEW_FEE_INSTALLMENTS");
    }
    if (listWidgetGroup === "students") {
      return hasPermission("VIEW_STUDENTS");
    }
    if (listWidgetGroup === "batches") {
      return hasPermission("VIEW_BATCHES||VIEW_SELF_BATCHES");
    }
    if (listWidgetGroup === "leads") {
      return hasPermission("VIEW_LEADS");
    }
    if (listWidgetGroup === "training_programs") {
      return hasPermission("VIEW_TRAINING_PROGRAMS");
    }
    return false;
  }, [listWidgetGroup, Permission]);

  const listWidgets = useMemo(() => {
    if (!listWidgetGroup || !canShowListWidgets) return [];
    const group = leadsSummary?.dashboard_widgets?.[listWidgetGroup];
    return Array.isArray(group) ? group : [];
  }, [leadsSummary, listWidgetGroup, canShowListWidgets]);

  const formatListWidgetValue = (widget) => {
    if (!widget) return "—";
    if (widget.display === "currency") {
      const value = Number(widget.value);
      if (!Number.isFinite(value)) return "—";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(value);
    }
    const num = Number(widget.value);
    if (!Number.isFinite(num)) return widget.value ?? "—";
    return num.toLocaleString("en-IN");
  };

  const isListWidgetActive = (widget) => {
    if (!widget?.query) return false;
    const filterKeys = Object.entries(widget.query).filter(
      ([key]) =>
        key !== "page" &&
        key !== "pageSize" &&
        key !== "sortBy" &&
        key !== "sortOrder"
    );
    if (!filterKeys.length) return false;
    return filterKeys.every(([key, value]) => {
      const expected = String(value ?? "").trim().toLowerCase();
      if (!expected) return true;
      return (
        String(searchParams.get(key) || "")
          .trim()
          .toLowerCase() === expected
      );
    });
  };

  const applyListWidgetFilter = (widget) => {
    if (!widget) return;
    const targetPath = String(widget.list_path || "").trim();
    const params = new URLSearchParams();
    Object.entries(widget.query || {}).forEach(([key, value]) => {
      const text = String(value ?? "").trim();
      if (text) params.set(key, text);
    });
    const query = params.toString();
    if (targetPath && targetPath !== basePath) {
      navigate(`${targetPath}${query ? `?${query}` : ""}`);
      return;
    }
    setSearchParams(params, { replace: false });
  };

  const leadsListSubtitle = useMemo(() => {
    if (!isLeadsApiList || isLeadActivityReport) return null;
    const total =
      leadsSummary?.total_leads ?? paginationData?.total ?? 0;
    const enrolled =
      leadsSummary?.leads_funnel_metrics?.enrolled_leads_this_month?.current ?? 0;
    return `${total} total leads • ${enrolled} enrolled this cycle`;
  }, [isLeadsApiList, isLeadActivityReport, leadsSummary, paginationData?.total]);
  const isBatchStudentsApiList = apiEndpoint?.includes("/api/ems/batch-students");
  const isClassRecordsApiList = apiEndpoint?.includes("/api/ems/class-records");
  const isClassesAttendanceApiList =
    apiEndpoint?.includes("/api/ems/classes-attendance");
  const selectedLeadsTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedLeadsLeadStage = (() => {
    const raw = String(leadStageFilter || "").trim();
    if (!raw) return "All";
    const matched = (leadStageOptions || []).find(
      (stage) =>
        String(stage.id) === raw ||
        String(stage.name || "").trim().toLowerCase() === raw.toLowerCase()
    );
    return matched ? String(matched.id) : raw;
  })();
  const selectedBatchTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedBatchStatus = String(statusFilter || "").trim() || "All";
  const selectedBatchMode = String(batchModeFilter || "").trim() || "All";
  const selectedDemoSessionsTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedOffersTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedTestsTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedAssessmentsTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedAssessmentsType = (() => {
    const raw = String(searchParams.get("organization_ems_assessment_type_id") || "").trim();
    if (!raw || raw.toLowerCase() === "all") return "All";
    return raw;
  })();
  const selectedBatchTrainerBatch = (() => {
    const raw = String(searchParams.get("organization_ems_batch_id") || "").trim();
    if (!raw || raw.toLowerCase() === "all") return "All";
    return raw;
  })();
  const selectedBatchTrainerTrainer = (() => {
    const raw = String(searchParams.get("trainer_employee_id") || "").trim();
    if (!raw || raw.toLowerCase() === "all") return "All";
    return raw;
  })();
  const selectedLeadStageStatus = (() => {
    const raw = String(leadStatusFilter || "").trim().toLowerCase();
    if (raw === "open" || raw === "1" || raw === "true") return "Open";
    if (raw === "closed" || raw === "0" || raw === "false") return "Closed";
    return "All";
  })();
  const selectedStudentReviewStatus = (() => {
    const raw = String(searchParams.get("review_status") || "").trim();
    if (!raw || raw.toLowerCase() === "all") return "All";
    return raw;
  })();
  const selectedFeeReceiptStudent = (() => {
    const raw = String(searchParams.get("organization_ems_student_id") || "").trim();
    if (!raw || raw.toLowerCase() === "all") return "All";
    return raw;
  })();
  const selectedDemoParticipantsTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedDemoParticipantsAttendance = (() => {
    const value = String(attendanceStatusFilter || "").trim();
    if (!value || value.toLowerCase() === "all") return "All";
    return value;
  })();
  const selectedDemoSessionsMode = (() => {
    const value = String(batchModeFilter || searchParams.get("demo_mode") || "").trim();
    if (!value || value.toLowerCase() === "all") return "All";
    return value;
  })();
  const selectedBatchStudentsStatus = (() => {
    const t = String(batchStatusFilter || "").trim();
    if (t === "") return "All";
    if (t.toLowerCase() === "all") return "All";
    return t;
  })();
  const selectedBatchStudentsMode = String(batchModeFilter || "").trim() || "All";
  const selectedBatchStudentsTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedClassRecordsTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedClassRecordsStatus = String(statusFilter || "").trim() || "All";
  const selectedClassesAttendanceStatus = (() => {
    const t = String(attendanceStatusFilter || "").trim();
    if (t === "Present" || t === "Absent") return t;
    return "";
  })();
  const selectedBatchClassesTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedBatchClassActivity = (() => {
    const value = String(searchParams.get("batch_activity") || "active")
      .trim()
      .toLowerCase();
    return value === "inactive" ? "inactive" : "active";
  })();
  const handleBatchClassesTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleBatchClassActivityChange = (_event, value) => {
    if (value !== "active" && value !== "inactive") return;
    const next = new URLSearchParams(searchParams);
    next.set("batch_activity", value);
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleBatchStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("status", value);
    else next.delete("status");
    setSearchParams(next);
  };
  const handleBatchModeFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("mode", value);
    else next.delete("mode");
    setSearchParams(next);
  };
  const handleTestsTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleAssessmentsTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleAssessmentsTypeFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("organization_ems_assessment_type_id", value);
    else next.delete("organization_ems_assessment_type_id");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleBatchTrainerBatchFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("organization_ems_batch_id", value);
    else next.delete("organization_ems_batch_id");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleBatchTrainerTrainerFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("trainer_employee_id", value);
    else next.delete("trainer_employee_id");
    next.set("page", "1");
    setSearchParams(next);
  };
  const selectedOfferAudienceOffer = (() => {
    const raw = String(searchParams.get("organization_ems_offer_id") || "").trim();
    if (!raw || raw.toLowerCase() === "all") return "All";
    return raw;
  })();
  const selectedOfferAudienceScope = (() => {
    const raw = String(searchParams.get("recipient_type") || "").trim().toUpperCase();
    if (raw === "INTERNAL" || raw === "EXTERNAL") return raw;
    return "All";
  })();
  const selectedContactMode = (() => {
    const raw = String(searchParams.get("preferred_contact_mode") || "").trim();
    if (!raw || raw.toLowerCase() === "all") return "All";
    return raw;
  })();
  const selectedContactTimezone = (() => {
    const raw = String(searchParams.get("preferred_contact_timezone") || "").trim();
    if (!raw || raw.toLowerCase() === "all") return "All";
    return raw;
  })();
  const selectedLeadActivityDirection = (() => {
    const raw = String(searchParams.get("lead_direction") || "").trim();
    if (!raw || raw.toLowerCase() === "all") return "All";
    return raw;
  })();
  const selectedLeadActivityStatus = (() => {
    const raw = String(searchParams.get("status") || "").trim();
    if (!raw || raw.toLowerCase() === "all") return "All";
    return raw;
  })();
  const handleLeadActivityFilterChange = (param) => (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set(param, value);
    else next.delete(param);
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleLeadStageStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("lead_status", value);
    else next.delete("lead_status");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleStudentReviewStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("review_status", value);
    else next.delete("review_status");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleFeeReceiptStudentFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("organization_ems_student_id", value);
    else next.delete("organization_ems_student_id");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleOffersTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    next.delete("training_program_id");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleDemoParticipantsTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    next.delete("training_program_id");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleDemoParticipantsAttendanceFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("attendance_status", value);
    else next.delete("attendance_status");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleDemoSessionsTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    next.delete("training_program_id");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleDemoSessionsModeFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("mode", value);
    else next.delete("mode");
    next.delete("demo_mode");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleLeadsTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    next.delete("training_program_id");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleLeadsLeadStageFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("lead_stage", value);
    else next.delete("lead_stage");
    next.set("page", "1");
    setSearchParams(next);
  };
  const handleBatchTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    setSearchParams(next);
  };
  const handleBatchStudentsStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value === "All") next.set("batch_status", "all");
    else if (value) next.set("batch_status", value);
    else next.delete("batch_status");
    setSearchParams(next);
  };
  const handleBatchStudentsModeFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("mode", value);
    else next.delete("mode");
    setSearchParams(next);
  };
  const handleBatchStudentsTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    setSearchParams(next);
  };
  const handleClassRecordsTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    setSearchParams(next);
  };
  const handleClassRecordsStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("status", value);
    else next.delete("status");
    setSearchParams(next);
  };
  const handleClassesAttendanceStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value === "Present" || value === "Absent") {
      next.set("attendance_status", value);
    } else {
      next.delete("attendance_status");
    }
    setSearchParams(next);
  };

  const selectedFeeInstallmentStatus = (() => {
    const t = String(statusFilter ?? "").trim();
    if (!t) return "All";
    const tl = t.toLowerCase();
    const allowed = ["pending", "paid", "overdue", "cancelled"];
    if (!allowed.includes(tl)) return "All";
    return tl.charAt(0).toUpperCase() + tl.slice(1);
  })();
  const selectedFeeInstallmentDuePeriod = (() => {
    const t = String(searchParams.get("due_period") ?? "").trim();
    return t || "All";
  })();
  const selectedStudentsAdmissionPeriod = (() => {
    const t = String(searchParams.get("admission_period") ?? "").trim();
    return t || "All";
  })();
  const rawStudentsTrainingProgramFilter = String(trainingProgramFilter || "").trim();
  const selectedStudentsTrainingProgram = (() => {
    if (!rawStudentsTrainingProgramFilter) return "All";
    const isNumeric =
      String(Number(rawStudentsTrainingProgramFilter)) === rawStudentsTrainingProgramFilter;
    if (isNumeric) return rawStudentsTrainingProgramFilter;
    const matched = (trainingProgramsAllOptions || []).find(
      (p) =>
        String(p?.name || "").trim().toLowerCase() ===
        rawStudentsTrainingProgramFilter.toLowerCase()
    );
    return matched ? String(matched.id) : rawStudentsTrainingProgramFilter;
  })();
  const hasAdhocStudentsTrainingProgramOption =
    selectedStudentsTrainingProgram !== "All" &&
    !(trainingProgramsAllOptions || []).some(
      (p) => String(p?.id ?? "").trim() === selectedStudentsTrainingProgram
    );

  const handleFeeInstallmentDuePeriodFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") {
      next.set("due_period", value);
    } else {
      next.delete("due_period");
    }
    setSearchParams(next);
  };

  const handleFeeInstallmentStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") {
      next.set("status", value.toLowerCase());
    } else {
      next.delete("status");
    }
    setSearchParams(next);
  };
  const handleStudentsAdmissionPeriodChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") {
      next.set("admission_period", value);
    } else {
      next.delete("admission_period");
    }
    setSearchParams(next);
  };
  const handleStudentsTrainingProgramChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") {
      next.set("training_program", value);
    } else {
      next.delete("training_program");
    }
    setSearchParams(next);
  };

  const handleSendOfferAudienceMail = async (row) => {
    const id = row?.organization_ems_offer_audience_id ?? row?.id;
    if (id == null || String(id).trim() === "" || String(id) === "-") {
      toast.error("Cannot send the offer email for this row.");
      return;
    }
    try {
      const res = await axios.post(
        `${MAIN_URL}/api/ems/offer-audiences/${id}/send-email`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      const email = res.data?.email || {};
      const message = email.message || res.data?.message || "Offer email sent.";
      if (Number(email.sent) > 0) toast.success(message);
      else toast.error(message);
    } catch (error) {
      toast.error(
        error?.response?.data?.email?.message ||
          error?.response?.data?.message ||
          "The offer email was not sent."
      );
    }
  };

  const handleSnapshot = (row) => {
    const leadId = row?.organization_ems_lead_id ?? row?.id ?? row?.lead_id;
    if (
      leadId === undefined ||
      leadId === null ||
      String(leadId).trim() === "" ||
      String(leadId) === "-"
    ) {
      return;
    }

    const leadIds = (rowsForTable || [])
      .map((r) => r?.organization_ems_lead_id ?? r?.id ?? r?.lead_id)
      .filter(
        (id) =>
          id !== undefined &&
          id !== null &&
          String(id).trim() !== "" &&
          String(id) !== "-"
      )
      .map((id) => String(id));

    navigate(`/lead-activities/snapshot/${encodeURIComponent(String(leadId))}`, {
      state: { leadIds },
    });
  };

  const isEmsApiList = Boolean(apiEndpoint?.includes("/api/ems/"));

  const deleteWithCascade = async (id, cascade = false) => {
    const response = await axios.delete(`${MAIN_URL}${apiEndpoint}/${id}`, {
      params: cascade ? { cascade: 1 } : {},
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response;
  };

  const showRelatedDeleteToast = (id, data, title = "Related data found") => {
    const message =
      data?.message ||
      "This record is linked to other data. Confirm to delete everything together.";
    const summary = Array.isArray(data?.related_summary)
      ? data.related_summary
      : [];

    toast.custom(
      (t) => (
        <Box
          sx={{
            maxWidth: 400,
            p: 2,
            bgcolor: "background.paper",
            boxShadow: 4,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "warning.main",
            opacity: t.visible ? 1 : 0,
            transition: "opacity 0.2s",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} color="warning.dark">
            {title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "text.primary" }}>
            {message}
          </Typography>
          {summary.length > 0 ? (
            <Box
              component="ul"
              sx={{ mt: 1, mb: 0.5, pl: 2.5, color: "text.secondary" }}
            >
              {summary.map((line) => (
                <Typography
                  component="li"
                  variant="body2"
                  key={line}
                  sx={{ mb: 0.25 }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          ) : null}
          <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
            <Button size="small" onClick={() => toast.dismiss(t.id)}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={async () => {
                toast.dismiss(t.id);
                try {
                  await deleteWithCascade(id, true);
                  toast.success(
                    data?.message?.includes("linked")
                      ? "Record and all related data deleted successfully."
                      : "Deleted successfully."
                  );
                  fetchData(false);
                } catch (err) {
                  toast.error(
                    err?.response?.data?.message ||
                      err?.response?.data?.error ||
                      "Failed to delete."
                  );
                }
              }}
            >
              Delete all
            </Button>
          </Box>
        </Box>
      ),
      { duration: Infinity, position: "top-center" }
    );
  };

  // ✅ Delete function (EMS lists support related-record guard + cascade)
  const resolveDeleteId = (idOrRow) => {
    if (idOrRow == null || idOrRow === "") return null;
    if (typeof idOrRow === "object" && !Array.isArray(idOrRow)) {
      if (idOrRow.__deleteAllModuleQuestions) {
        return (
          idOrRow.organization_ems_training_program_module_id ??
          idOrRow.id ??
          null
        );
      }
      return (
        idOrRow[tablePrimaryKey] ??
        idOrRow.organization_ems_batch_id ??
        idOrRow.id ??
        null
      );
    }
    return idOrRow;
  };

  const deleteFunction = async (idOrRow, options = {}) => {
    const isDeleteAllQuestions =
      Boolean(options?.deleteAllModuleQuestions) ||
      Boolean(idOrRow?.__deleteAllModuleQuestions);
    if (isDeleteAllQuestions && !showDeleteButton) {
      toast.error("You do not have permission to delete questions.");
      return;
    }
    const id = resolveDeleteId(idOrRow);
    if (id == null || id === "" || Number.isNaN(Number(id))) {
      toast.error("Cannot delete: invalid record ID.");
      return;
    }
    const cascade = Boolean(options?.cascade);
    try {
      if (isDeleteAllQuestions) {
        await axios.delete(
          `${MAIN_URL}/api/ems/module-questions/by-module/${id}`,
          {
            params: {
              organization_id: organizationId,
              ...(cascade ? { cascade: 1 } : {}),
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        toast.success("All questions in this module were deleted.");
        fetchData(false);
        return;
      }
      if (isEmsApiList) {
        try {
          await deleteWithCascade(id, cascade);
        } catch (error) {
          if (error?.response?.status === 409 && !cascade) {
            showRelatedDeleteToast(
              id,
              error.response?.data || {},
              isBatchesList ? "Batch has related data" : "Related data found"
            );
            const cascadeRequiredError = new Error(
              "Related records require cascade delete."
            );
            cascadeRequiredError.code = "EMS_CASCADE_REQUIRED";
            throw cascadeRequiredError;
          }
          throw error;
        }
      } else {
        await axios.delete(`${MAIN_URL}${apiEndpoint}/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }

      if (isEmsApiList && cascade) {
        toast.success("Record and all related data deleted successfully.");
      } else if (isEmsApiList) {
        toast.success("Deleted successfully.");
      }
      fetchData(false);
    } catch (error) {
      console.error("Delete error:", error);
      const apiMessage =
        error?.response?.data?.message || error?.response?.data?.error;
      if (apiMessage) {
        toast.error(apiMessage);
      }
      throw error;
    }
  };

  return (
    <>
      <Layout4
        heading={title}
        Route={addRoute}
        btnName={addButtonLabel || (isLeadsApiList ? "New Lead" : `Add ${title}`)}
        subtitle={leadsListSubtitle}
        showAddButton={showAddButton}
        add_action={actions.add}
        headerExtra={
          isLeadsApiList && !isLeadActivityReport ? (
            <button
              type="button"
              className={`ems-csl-closed-toggle${
                String(leadStatusFilter || "").trim().toLowerCase() === "all"
                  ? " is-on"
                  : ""
              }`}
              onClick={() => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  const showingClosed =
                    String(next.get("lead_status") || "")
                      .trim()
                      .toLowerCase() === "all";
                  next.set("lead_status", showingClosed ? "open" : "all");
                  next.set("page", "1");
                  return next;
                });
              }}
            >
              {String(leadStatusFilter || "").trim().toLowerCase() === "all"
                ? "Hide closed"
                : "Closed leads"}
            </button>
          ) : null
        }
      />
      <div className="ems-section-divider" />

      {summaryCardsEnabled && listWidgets.length > 0 && (
        <section
          className="ems-stat-cards-brand ems-list-widgets"
          style={{ marginBottom: 16 }}
        >
          <div
            className={`ems-grid ${
              listWidgets.length >= 5 ? "ems-g-5" : "ems-g-4"
            }`}
          >
            {listWidgets.map((widget) => {
              const active = isListWidgetActive(widget);
              const visual =
                LIST_WIDGET_VISUALS[widget.key] || {
                  icon: Boxes,
                  tone: "",
                };
              const Icon = visual.icon;
              return (
                <div
                  key={widget.key || widget.label}
                  className={`ems-card ems-stat${active ? " is-active" : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => applyListWidgetFilter(widget)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      applyListWidgetFilter(widget);
                    }
                  }}
                >
                  <div className="ems-stat-top">
                    <div
                      className={`ems-stat-icon${
                        visual.tone ? ` ${visual.tone}` : ""
                      }`}
                    >
                      <Icon size={20} />
                    </div>
                  </div>
                  <div className="ems-stat-label">{widget.label}</div>
                  <div
                    className={`ems-stat-value ems-tnum${
                      visual.valueTone ? ` ${visual.valueTone}` : ""
                    }`}
                  >
                    {formatListWidgetValue(widget)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {basePath === "/program-monthly-traffic" && organizationId ? (
        <ProgramMonthlyTrafficCharts organizationId={organizationId} />
      ) : null}

      {!loadingConfig && basePath === "/batch-modules" ? (
        <BatchModulesGroupedView organizationId={organizationId} />
      ) : null}

      {!loadingConfig && basePath === "/batch-trainer-modules" ? (
        <TrainerModulesGroupedView organizationId={organizationId} />
      ) : null}

      {!loadingConfig && basePath !== "/batch-modules" && basePath !== "/batch-trainer-modules" && (
      <TableDataGeneric
        key={
          isFeeInstallmentsList
            ? `fee-installments-${feeInstallmentFetchKey || "all"}`
            : undefined
        }
        tableName={tableName || title}
        primaryKey={tablePrimaryKey}
        heading={title}
        data={displayRowsForTable}
        loading={loading}
        configss={configColumns}
        loadedBackendConfig={tableConfig}
        datagridKey={datagridKey}
        sortname={tablePrimaryKey}
        Route={editRouteBase}
        mainKey={tablePrimaryKey}
        DeleteFunc={deleteFunction}
        EditFunc={(item) => {
          const recordId =
            item?.organization_ems_module_question_id ||
            item?.organization_ems_training_program_module_id ||
            item?.id;
          navigate(`${editRouteBase}/${recordId}`);
        }}
        ViewFunc={(item) =>
          navigate(
            `${editRouteBase.replace("/edit", "/view")}/${item.id}`
          )
        }
        token={localStorage.getItem("token")}
        showViewButton={effectiveShowViewButton}
        showEditButton={showEditButton}
        showDeleteButton={showDeleteButton}
        edit_delete_action={editDeleteActions}
        showReceiptButton={showReceiptButton}
        ReceiptFunc={handleReceipt}
        showEnrolledButton={showEnrolledButton}
        EnrolledFunc={handleEnrolled}
        showSnapshotButton={showSnapshotButton}
        SnapshotFunc={handleSnapshot}
        showLeadActivityButton={showLeadActivityButton}
        LeadActivityFunc={handleLeadActivity}
        showAdmissionButton={showAdmissionButton}
        AdmissionFunc={handleAdmission}
        showDemoParticipantButton={showDemoParticipantButton}
        DemoParticipantFunc={handleDemoParticipant}
        showFeePortalButton={showFeePortalButton}
        FeePortalFunc={handleFeePortal}
        showStudentAttendanceButton={showStudentAttendanceButton}
        StudentAttendanceFunc={handleStudentAttendance}
        onStudentNameClick={
          isStudentsList ? handleStudentNameClick : undefined
        }
        onTrainingProgramNameClick={
          isTrainingProgramsList ? handleTrainingProgramNameClick : undefined
        }
        showBatchAttendanceButton={showBatchAttendanceButton}
        BatchAttendanceFunc={handleBatchAttendanceMarking}
        showBatchClassesButton={showBatchClassesButton}
        BatchClassesFunc={handleBatchClassesFromBatch}
        showBatchStudentsButton={showBatchStudentsButton}
        BatchStudentsFunc={handleBatchStudentsFromBatch}
        showBatchRecordsButton={showBatchRecordsButton}
        showSendOfferMailButton={
          basePath === "/offer-audiences" && hasPermission("EDIT_OFFER_AUDIENCE")
        }
        SendOfferMailFunc={handleSendOfferAudienceMail}
        BatchRecordsFunc={handleBatchRecordsFromBatchClass}
        serverSearchEnabled={isFeeInstallmentsList}
        serverSearchValue={feeInstallmentSearchInput}
        onServerSearchChange={setFeeInstallmentSearchInput}
        filterModule={useMetadataFilters ? filterModule || tableName : undefined}
        paginationData={useMetadataFilters ? paginationData : undefined}
        onSortChange={useMetadataFilters ? handleMetadataSortChange : undefined}
        onPageChange={useMetadataFilters ? handleMetadataPageChange : undefined}
        hideFilterButton={hideFilterButton}
        hidePersonalizeView={hidePersonalizeView}
        toolbarCenterContent={
          isLeadsList ? (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "minmax(180px, 280px) minmax(160px, 220px)",
                },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedLeadsTrainingProgram}
                onChange={handleLeadsTrainingProgramFilterChange}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Lead Stage"
                value={selectedLeadsLeadStage}
                onChange={handleLeadsLeadStageFilterChange}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {leadStageOptions.map((stage) => (
                  <MenuItem key={String(stage.id)} value={String(stage.id)}>
                    {stage.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : isDemoSessionsList ? (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "minmax(180px, 280px) minmax(140px, 180px)",
                },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedDemoSessionsTrainingProgram}
                onChange={handleDemoSessionsTrainingProgramFilterChange}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Mode"
                value={selectedDemoSessionsMode}
                onChange={handleDemoSessionsModeFilterChange}
                sx={{ minWidth: 0, width: "100%" }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Offline">Offline</MenuItem>
                <MenuItem value="Hybrid">Hybrid</MenuItem>
              </TextField>
            </Box>
          ) : isDemoParticipantsList ? (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "minmax(180px, 280px) minmax(150px, 200px)",
                },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedDemoParticipantsTrainingProgram}
                onChange={handleDemoParticipantsTrainingProgramFilterChange}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Attendance"
                value={selectedDemoParticipantsAttendance}
                onChange={handleDemoParticipantsAttendanceFilterChange}
                sx={{ minWidth: 0, width: "100%" }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
                <MenuItem value="Attended">Attended</MenuItem>
                <MenuItem value="Absent">Absent</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </TextField>
            </Box>
          ) : isAssessmentsList ? (
            <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedAssessmentsTrainingProgram}
                onChange={handleAssessmentsTrainingProgramFilterChange}
                sx={{
                  width: 280,
                  minWidth: 280,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Assessment Type"
                value={selectedAssessmentsType}
                onChange={handleAssessmentsTypeFilterChange}
                sx={{
                  width: 240,
                  minWidth: 240,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {assessmentTypeOptions.map((type) => (
                  <MenuItem key={String(type.id)} value={String(type.id)}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : isTestsList ? (
            <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedTestsTrainingProgram}
                onChange={handleTestsTrainingProgramFilterChange}
                sx={{
                  width: 280,
                  minWidth: 280,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : isLeadActivitiesList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Lead Direction"
                  value={selectedLeadActivityDirection}
                  onChange={handleLeadActivityFilterChange("lead_direction")}
                  sx={{
                    width: 200,
                    minWidth: 200,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Incoming">Incoming</MenuItem>
                  <MenuItem value="Outgoing">Outgoing</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={selectedLeadActivityStatus}
                  onChange={handleLeadActivityFilterChange("status")}
                  sx={{
                    width: 200,
                    minWidth: 200,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Attended">Attended</MenuItem>
                  <MenuItem value="Missed">Missed</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                  <MenuItem value="No Answer">No Answer</MenuItem>
                </TextField>
              </Box>
            </Box>
          ) : isLeadContactTimingsList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Contact Mode"
                  value={selectedContactMode}
                  onChange={handleLeadActivityFilterChange("preferred_contact_mode")}
                  sx={{
                    width: 200,
                    minWidth: 200,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {["Call", "WhatsApp", "Email", "SMS", "Telegram", "Visit", "Other"].map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 240, minWidth: 240, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Timezone"
                  value={selectedContactTimezone}
                  onChange={handleLeadActivityFilterChange("preferred_contact_timezone")}
                  sx={{
                    width: 240,
                    minWidth: 240,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {contactTimingTimezoneOptions.map((zone) => (
                    <MenuItem key={zone} value={zone}>
                      {zone}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          ) : isRecruitmentAgenciesList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 240, minWidth: 240, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Agency Name"
                  value={(() => {
                    const raw = String(searchParams.get("agency_name") || "").trim();
                    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("agency_name")}
                  sx={{
                    width: 240,
                    minWidth: 240,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {recruitmentAgencyNameOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 180, minWidth: 180, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Country"
                  value={(() => {
                    const raw = String(searchParams.get("country") || "").trim();
                    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("country")}
                  sx={{
                    width: 180,
                    minWidth: 180,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {recruitmentAgencyCountryOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          ) : isPlacementPoolList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Course"
                  value={selectedTestsTrainingProgram}
                  onChange={handleTestsTrainingProgramFilterChange}
                  sx={{
                    width: 280,
                    minWidth: 280,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {trainingProgramsAllOptions.map((program) => (
                    <MenuItem key={String(program.id)} value={String(program.id)}>
                      {program.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Batch Student Status"
                  value={(() => {
                    const raw = String(searchParams.get("batch_student_status") || "").trim();
                    if (!raw || raw.toLowerCase() === "all") return "All";
                    const match = ["Pursuing", "Completed", "Dropped", "On Hold"].find(
                      (option) => option.toLowerCase() === raw.toLowerCase()
                    );
                    return match || raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("batch_student_status")}
                  sx={{
                    width: 200,
                    minWidth: 200,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Pursuing">Pursuing</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Dropped">Dropped</MenuItem>
                  <MenuItem value="On Hold">On Hold</MenuItem>
                </TextField>
              </Box>
            </Box>
          ) : isPlacementReferralsList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Training Program"
                  value={selectedTestsTrainingProgram}
                  onChange={handleTestsTrainingProgramFilterChange}
                  sx={{
                    width: 280,
                    minWidth: 280,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {trainingProgramsAllOptions.map((program) => (
                    <MenuItem key={String(program.id)} value={String(program.id)}>
                      {program.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 240, minWidth: 240, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Recruitment Agency"
                  value={(() => {
                    const raw = String(
                      searchParams.get("organization_ems_recruitment_agency_id") || ""
                    ).trim();
                    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
                  })()}
                  onChange={handleLeadActivityFilterChange(
                    "organization_ems_recruitment_agency_id"
                  )}
                  sx={{
                    width: 240,
                    minWidth: 240,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {placementReferralAgencyOptions.map((agency) => (
                    <MenuItem key={String(agency.id)} value={String(agency.id)}>
                      {agency.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          ) : isPlacementAssistanceList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Admission"
                  value={(() => {
                    const raw = String(
                      searchParams.get("organization_ems_admission_id") || ""
                    ).trim();
                    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("organization_ems_admission_id")}
                  sx={{
                    width: 280,
                    minWidth: 280,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {placementAssistanceAdmissionOptions.map((admission) => (
                    <MenuItem key={String(admission.id)} value={String(admission.id)}>
                      {admission.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 180, minWidth: 180, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Overall Status"
                  value={(() => {
                    const raw = String(searchParams.get("overall_status") || "").trim();
                    if (!raw || raw.toLowerCase() === "all") return "All";
                    const match = ["Pending", "In Progress", "Completed"].find(
                      (option) => option.toLowerCase() === raw.toLowerCase()
                    );
                    return match || "All";
                  })()}
                  onChange={handleLeadActivityFilterChange("overall_status")}
                  sx={{
                    width: 180,
                    minWidth: 180,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </TextField>
              </Box>
            </Box>
          ) : isMockInterviewsList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 240, minWidth: 240, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Interview Type"
                  value={(() => {
                    const raw = String(searchParams.get("interview_type") || "").trim();
                    if (!raw || raw.toLowerCase() === "all") return "All";
                    const match = [
                      "Technical Fundamentals",
                      "Advanced Technical",
                      "Final Job Readiness",
                    ].find((option) => option.toLowerCase() === raw.toLowerCase());
                    return match || raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("interview_type")}
                  sx={{
                    width: 240,
                    minWidth: 240,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Technical Fundamentals">Technical Fundamentals</MenuItem>
                  <MenuItem value="Advanced Technical">Advanced Technical</MenuItem>
                  <MenuItem value="Final Job Readiness">Final Job Readiness</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Result"
                  value={(() => {
                    const raw = String(searchParams.get("result") || "").trim();
                    if (!raw || raw.toLowerCase() === "all") return "All";
                    const match = ["Passed", "Needs Improvement"].find(
                      (option) => option.toLowerCase() === raw.toLowerCase()
                    );
                    return match || raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("result")}
                  sx={{
                    width: 200,
                    minWidth: 200,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Passed">Passed</MenuItem>
                  <MenuItem value="Needs Improvement">Needs Improvement</MenuItem>
                </TextField>
              </Box>
            </Box>
          ) : isCompaniesList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 220, minWidth: 220, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Industry"
                  value={(() => {
                    const raw = String(searchParams.get("industry") || "").trim();
                    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("industry")}
                  sx={{
                    width: 220,
                    minWidth: 220,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {companyIndustryOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 160, minWidth: 160, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={(() => {
                    const raw = String(searchParams.get("status") || "").trim();
                    if (!raw || raw.toLowerCase() === "all") return "All";
                    if (raw.toLowerCase() === "inactive") return "Inactive";
                    if (raw.toLowerCase() === "active") return "Active";
                    return raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("status")}
                  sx={{
                    width: 160,
                    minWidth: 160,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
              </Box>
            </Box>
          ) : isLeadStagesList ? (
            <Box sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Lead Status"
                value={selectedLeadStageStatus}
                onChange={handleLeadStageStatusFilterChange}
                sx={{
                  width: 200,
                  minWidth: 200,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </TextField>
            </Box>
          ) : isStudentReviewsList ? (
            <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Student"
                value={selectedFeeReceiptStudent}
                onChange={handleFeeReceiptStudentFilterChange}
                sx={{
                  width: 280,
                  minWidth: 280,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {feeReceiptStudentOptions.map((student) => (
                  <MenuItem key={String(student.id)} value={String(student.id)}>
                    {student.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Review Status"
                value={selectedStudentReviewStatus}
                onChange={handleStudentReviewStatusFilterChange}
                sx={{
                  width: 220,
                  minWidth: 220,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Requested">Requested</MenuItem>
                <MenuItem value="Submitted">Submitted</MenuItem>
                <MenuItem value="Verified">Verified</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </TextField>
            </Box>
          ) : isCertificatesList ? (
            <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Batch"
                value={selectedBatchTrainerBatch}
                onChange={handleBatchTrainerBatchFilterChange}
                sx={{
                  width: 240,
                  minWidth: 240,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {batchTrainerBatchOptions.map((batch) => (
                  <MenuItem key={String(batch.id)} value={String(batch.id)}>
                    {batch.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedTestsTrainingProgram}
                onChange={handleTestsTrainingProgramFilterChange}
                sx={{
                  width: 280,
                  minWidth: 280,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : isTrainingProgramModulesList || isModuleQuestionsList ? (
            <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedTestsTrainingProgram}
                onChange={handleTestsTrainingProgramFilterChange}
                sx={{
                  width: 280,
                  minWidth: 280,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : isAssessmentSchedulesList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Batch"
                  value={selectedBatchTrainerBatch}
                  onChange={handleBatchTrainerBatchFilterChange}
                  sx={{
                    width: 280,
                    minWidth: 280,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {batchTrainerBatchOptions.map((batch) => (
                    <MenuItem key={String(batch.id)} value={String(batch.id)}>
                      {batch.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 180, minWidth: 180, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={(() => {
                    const raw = String(searchParams.get("status") || "").trim();
                    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("status")}
                  sx={{
                    width: 180,
                    minWidth: 180,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </TextField>
              </Box>
            </Box>
          ) : isAssessmentResultsList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Student"
                  value={selectedFeeReceiptStudent}
                  onChange={handleFeeReceiptStudentFilterChange}
                  sx={{
                    width: 280,
                    minWidth: 280,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {feeReceiptStudentOptions.map((student) => (
                    <MenuItem key={String(student.id)} value={String(student.id)}>
                      {student.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 180, minWidth: 180, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={(() => {
                    const raw = String(searchParams.get("result_status") || "").trim();
                    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("result_status")}
                  sx={{
                    width: 180,
                    minWidth: 180,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Pass">Pass</MenuItem>
                  <MenuItem value="Fail">Fail</MenuItem>
                  <MenuItem value="Absent">Absent</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </TextField>
              </Box>
            </Box>
          ) : isBatchTrainersList || isBatchClassTrainersList ? (
            <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Batch"
                value={selectedBatchTrainerBatch}
                onChange={handleBatchTrainerBatchFilterChange}
                sx={{
                  width: 240,
                  minWidth: 240,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {batchTrainerBatchOptions.map((batch) => (
                  <MenuItem key={String(batch.id)} value={String(batch.id)}>
                    {batch.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Trainer"
                value={selectedBatchTrainerTrainer}
                onChange={handleBatchTrainerTrainerFilterChange}
                sx={{
                  width: 240,
                  minWidth: 240,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {batchTrainerTrainerOptions.map((trainer) => (
                  <MenuItem key={String(trainer.id)} value={String(trainer.id)}>
                    {trainer.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : isBatchClassRecordTrainersList ? (
            <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Trainer"
                value={selectedBatchTrainerTrainer}
                onChange={handleBatchTrainerTrainerFilterChange}
                sx={{
                  width: 280,
                  minWidth: 280,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {batchTrainerTrainerOptions.map((trainer) => (
                  <MenuItem key={String(trainer.id)} value={String(trainer.id)}>
                    {trainer.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : isFeeReceiptsList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Student"
                  value={selectedFeeReceiptStudent}
                  onChange={handleFeeReceiptStudentFilterChange}
                  sx={{
                    width: 280,
                    minWidth: 280,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {feeReceiptStudentOptions.map((student) => (
                    <MenuItem key={String(student.id)} value={String(student.id)}>
                      {student.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 180, minWidth: 180, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Mode"
                  value={(() => {
                    const raw = String(searchParams.get("payment_mode") || "").trim();
                    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("payment_mode")}
                  sx={{
                    width: 180,
                    minWidth: 180,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {["UPI", "NetBanking", "Razorpay", "Cash", "Cheque"].map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          ) : isOfferAudiencesList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Offer"
                  value={selectedOfferAudienceOffer}
                  onChange={handleLeadActivityFilterChange("organization_ems_offer_id")}
                  sx={{
                    width: 280,
                    minWidth: 280,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {offerAudienceOfferOptions.map((offer) => (
                    <MenuItem key={String(offer.id)} value={String(offer.id)}>
                      {offer.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 180, minWidth: 180, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Scope"
                  value={selectedOfferAudienceScope}
                  onChange={handleLeadActivityFilterChange("recipient_type")}
                  sx={{
                    width: 180,
                    minWidth: 180,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="INTERNAL">Internal</MenuItem>
                  <MenuItem value="EXTERNAL">External</MenuItem>
                </TextField>
              </Box>
            </Box>
          ) : isTrainingProgramsList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <Box sx={{ width: 240, minWidth: 240, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Category"
                  value={(() => {
                    const raw = String(
                      searchParams.get("organization_ems_training_program_category_id") || ""
                    ).trim();
                    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
                  })()}
                  onChange={handleLeadActivityFilterChange(
                    "organization_ems_training_program_category_id"
                  )}
                  sx={{
                    width: 240,
                    minWidth: 240,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {trainingProgramCategoryOptions.map((category) => (
                    <MenuItem key={String(category.id)} value={String(category.id)}>
                      {category.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: 160, minWidth: 160, flexShrink: 0 }}>
                <TextField
                  select
                  size="small"
                  label="Months"
                  value={(() => {
                    const raw = String(searchParams.get("duration_in_months") || "").trim();
                    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
                  })()}
                  onChange={handleLeadActivityFilterChange("duration_in_months")}
                  sx={{
                    width: 160,
                    minWidth: 160,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {trainingProgramMonthOptions.map((months) => (
                    <MenuItem key={String(months)} value={String(months)}>
                      {months}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          ) : isAssessmentTypesList ? (
            <Box sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Delivery"
                value={(() => {
                  const raw = String(searchParams.get("assessment_delivery_mode") || "").trim();
                  return !raw || raw.toLowerCase() === "all" ? "All" : raw;
                })()}
                onChange={handleLeadActivityFilterChange("assessment_delivery_mode")}
                sx={{
                  width: 200,
                  minWidth: 200,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Submission">Submission</MenuItem>
                <MenuItem value="In Person">In Person</MenuItem>
              </TextField>
            </Box>
          ) : isOffersList ? (
            <Box sx={{ width: 280, minWidth: 280, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedOffersTrainingProgram}
                onChange={handleOffersTrainingProgramFilterChange}
                sx={{
                  width: 280,
                  minWidth: 280,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : isBatchesApiList ? (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr) minmax(0, 1fr)",
                  sm: "minmax(160px, 240px) minmax(110px, 160px) minmax(120px, 180px)",
                },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedBatchTrainingProgram}
                onChange={handleBatchTrainingProgramFilterChange}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  gridColumn: { xs: "1 / -1", sm: "auto" },
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Mode"
                value={selectedBatchMode}
                onChange={handleBatchModeFilterChange}
                sx={{ minWidth: 0, width: "100%" }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Offline">Offline</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Status"
                value={selectedBatchStatus}
                onChange={handleBatchStatusFilterChange}
                sx={{ minWidth: 0, width: "100%" }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
                <MenuItem value="Ongoing">Ongoing</MenuItem>
                <MenuItem value="On Hold">On Hold</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </TextField>
            </Box>
          ) : isBatchClassesList ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedBatchClassesTrainingProgram}
                onChange={handleBatchClassesTrainingProgramFilterChange}
                sx={{
                  width: 280,
                  minWidth: 280,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
            <div className="ems-seg" role="group" aria-label="Batches">
              <button
                type="button"
                className={selectedBatchClassActivity === "active" ? "active" : ""}
                onClick={() => handleBatchClassActivityChange(null, "active")}
              >
                Active
              </button>
              <button
                type="button"
                className={selectedBatchClassActivity === "inactive" ? "active" : ""}
                onClick={() => handleBatchClassActivityChange(null, "inactive")}
              >
                Inactive
              </button>
            </div>
            </Box>
          ) : isBatchStudentsApiList ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: 760,
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "minmax(260px, 1.5fr) minmax(170px, 1fr) minmax(170px, 1fr)",
                },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedBatchStudentsTrainingProgram}
                onChange={handleBatchStudentsTrainingProgramFilterChange}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Status"
                value={selectedBatchStudentsStatus}
                onChange={handleBatchStudentsStatusFilterChange}
                sx={{ minWidth: 0, width: "100%" }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Pursuing">Pursuing</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Dropped">Dropped</MenuItem>
                <MenuItem value="On Hold">On Hold</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Mode"
                value={selectedBatchStudentsMode}
                onChange={handleBatchStudentsModeFilterChange}
                sx={{ minWidth: 0, width: "100%" }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Offline">Offline</MenuItem>
              </TextField>
            </Box>
          ) : isClassRecordsApiList ? (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1.45fr) minmax(0, 1fr)",
                  sm: "minmax(160px, 260px) minmax(120px, 180px)",
                },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedClassRecordsTrainingProgram}
                onChange={handleClassRecordsTrainingProgramFilterChange}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Status"
                value={selectedClassRecordsStatus}
                onChange={handleClassRecordsStatusFilterChange}
                sx={{ minWidth: 0, width: "100%" }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
                <MenuItem value="Conducted">Conducted</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </TextField>
            </Box>
          ) : isClassesAttendanceApiList ? (
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <TextField
                select
                size="small"
                label="Attendance Status"
                value={selectedClassesAttendanceStatus}
                onChange={handleClassesAttendanceStatusFilterChange}
                displayEmpty
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                <MenuItem value="Present">Present</MenuItem>
                <MenuItem value="Absent">Absent</MenuItem>
              </TextField>
            </Box>
          ) : isFeeInstallmentsList ? (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1.45fr) minmax(0, 1fr)",
                  sm: "minmax(160px, 240px) minmax(120px, 180px)",
                },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                select
                size="small"
                label="Due Period"
                value={selectedFeeInstallmentDuePeriod}
                onChange={handleFeeInstallmentDuePeriodFilterChange}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {(config?.duePeriodOptions || []).map((option) => (
                  <MenuItem key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Status"
                value={selectedFeeInstallmentStatus}
                onChange={handleFeeInstallmentStatusFilterChange}
                sx={{ minWidth: 0, width: "100%" }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Overdue">Overdue</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </TextField>
            </Box>
          ) : isStudentsList || isAdmissionsApiList ? (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "minmax(160px, 280px) minmax(150px, 220px)",
                },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedStudentsTrainingProgram}
                onChange={handleStudentsTrainingProgramChange}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Admission Period"
                value={selectedStudentsAdmissionPeriod}
                onChange={handleStudentsAdmissionPeriodChange}
                sx={{ minWidth: 0, width: "100%" }}
              >
                <MenuItem value="All">All</MenuItem>
                {(config?.admissionPeriodOptions || []).map((option) => (
                  <MenuItem key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : null
        }
        config={resolvedTableConfig}
      />
      )}

      <AdmissionFeePortalDialog
        open={feePortalOpen}
        loading={feePortalLoading}
        admission={feePortalAdmission}
        organizationId={organizationId ?? org?.organization_id}
        onClose={() => {
          setFeePortalOpen(false);
          setFeePortalAdmission(null);
        }}
      />

      <BatchClassAttendanceViewDialog
        open={batchClassAttendanceDialog.open}
        batchClassId={batchClassAttendanceDialog.batchClassId}
        onClose={() =>
          setBatchClassAttendanceDialog({ open: false, batchClassId: null })
        }
      />

      <StudentAttendanceViewDialog
        open={studentAttendanceDialog.open}
        studentId={studentAttendanceDialog.studentId}
        studentName={studentAttendanceDialog.studentName}
        studentCode={studentAttendanceDialog.studentCode}
        onClose={() =>
          setStudentAttendanceDialog({
            open: false,
            studentId: null,
            studentName: "",
            studentCode: "",
          })
        }
      />

      <StudentDetailDialog
        open={studentDetailDialog.open}
        student={studentDetailDialog.student}
        onClose={() =>
          setStudentDetailDialog({ open: false, student: null })
        }
      />
    </>
  );
}

export default CommonList;
